import * as React from 'react';
import { createPortal } from 'react-dom';
import {
  ChevronLeft,
  Eye,
  Facebook,
  Instagram,
  MoreVertical,
  Play,
  Plus,
  Share2,
  Sparkles,
  Trash2,
  UploadCloud,
  X,
  Youtube,
  ArrowRight,
  Check,
  Clock3,
  Film,
  ImagePlus,
  Music2,
  Pause,
  RefreshCw,
  Wand2,
  School,
  MessageCircle,
  ClipboardList,
  PartyPopper,
  Trophy,
  BookOpen,
  CalendarDays,
  Download,
  AlertTriangle,
  Lightbulb,
  Palette,
  HeartHandshake,
  Goal,
  GraduationCap,
} from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { showGlobalModal } from 'core/components/GlobalModal';
import { PaginatedCardGrid } from 'core/components/PaginatedCardGrid';
import { SetupLoader } from 'core/components/SetupComponents/SetupLoader';
import { useAuth } from 'core/context/AuthContext/AuthContextProvider';
import { useDebounce } from 'core/hooks/useDebounce';
import { apiFileUpload } from 'core/utils/fileUpload/api';
import httpclient from 'libs/Axios';
import imageBg from 'assets/images/imageBG.webp';
import sampleImg from 'assets/images/sample.webp';
import {
  reelsApi,
  SocialHubStatus,
} from 'features/DigitalMarketing/api/reelsApi';
import { useImageEnhancer } from 'features/DigitalMarketing/hooks/useImageEnhancer';
import { DigitalMarketingBreadcrumb } from 'features/DigitalMarketing/components/DigitalMarketingBreadcrumb';
import { DigitalMarketingSearchInput } from 'features/DigitalMarketing/components/DigitalMarketingSearchInput';
import { DigitalMarketingDeleteModal } from 'features/DigitalMarketing/components/DigitalMarketingDeleteModal';
import { FieldLabel } from 'features/DigitalMarketing/components/FormControls';
import { ImagePreviewModal } from 'features/DigitalMarketing/components/ImagePreviewModal';
import { ManagementFilterBar } from 'features/DigitalMarketing/components/ManagementFilterBar';
import { Modal } from 'features/DigitalMarketing/components/ui/Modal';
import { Toggle } from 'features/DigitalMarketing/components/ui/Toggle';
import {
  editWebsiteContentInputClassName,
  editWebsiteContentLabelClassName,
  maxDescriptionGenerations,
} from 'features/DigitalMarketing/constants/formStyles';
import {
  extractUploadedUrl,
  getApiErrorMessage,
  getResponseList,
} from 'features/DigitalMarketing/utils/apiResponse';
import {
  buildReelsPath,
  REELS_BASE_PATH,
} from 'features/DigitalMarketing/routes/reelPaths';
import {
  DMButton,
  DMDiv,
  DMImage,
  DMInput,
  DMLabel,
  DMSelect,
  DMText,
  DMTextarea,
  DMBr,
  DMOption,
  DMVideo,
} from '../../components/Primitives';
import './reels.css';

type ReelStatus = 'generated' | 'posted' | 'generating';
type ReelFilter = 'both' | 'generated' | 'posted';

const reelFilterOptions = [
  { label: 'All Reels', value: 'both' as ReelFilter },
  { label: 'Generated Only', value: 'generated' as ReelFilter },
  { label: 'Posted Only', value: 'posted' as ReelFilter },
];

type ReelItem = {
  id: number;
  title: string;
  description: string;
  category: string;
  subCategory: string;
  style: string;
  images: string[];
  videoId: string;
  videoStatus: string;
  videoUrl: string;
  thumbnail: string;
  date: string;
  time: string;
  views: number;
  school: string;
  facebook: boolean;
  instagram: boolean;
  youtube: boolean;
  whatsapp: boolean;
  parentApp: boolean;
  facultyApp: boolean;
  hashtags: string;
  availablePlatforms: Array<'facebook' | 'instagram' | 'youtube'>;
  status: ReelStatus;
  raw: any;
};

const isReelPublished = (item: ReelItem) => {
  const postStatus = String(
    item.raw?.post_status || item.raw?.POST_STATUS || ''
  ).toUpperCase();
  return Boolean(
    item.status === 'posted' ||
    item.facebook ||
    item.instagram ||
    item.youtube ||
    item.whatsapp ||
    item.parentApp ||
    item.facultyApp ||
    ['PUBLISHED', 'POSTED', 'COMPLETED'].includes(postStatus)
  );
};

type ReelClientStatus = {
  sharedPlatforms?: string[];
  scheduledPlatforms?: string[];
  scheduledDate?: string;
  scheduledTime?: string;
};

const reelClientStatusStorageKey = 'digital-marketing-reel-client-status';
const pendingReelStorageKey = 'digital-marketing-pending-reel';

type PendingReelGeneration = {
  reelId: number;
  videoId: string;
  title: string;
};

const getPendingReelGeneration = (): PendingReelGeneration | null => {
  try {
    const value = window.sessionStorage.getItem(pendingReelStorageKey);
    return value ? JSON.parse(value) : null;
  } catch {
    return null;
  }
};

const savePendingReelGeneration = (pending: PendingReelGeneration) => {
  window.sessionStorage.setItem(pendingReelStorageKey, JSON.stringify(pending));
};

const clearPendingReelGeneration = () => {
  window.sessionStorage.removeItem(pendingReelStorageKey);
};
const reelSharePlatforms = [
  { key: 'instagram', name: 'Instagram', mono: 'IG', chip: 'linear-gradient(135deg,#f9ce34,#ee2a7b 55%,#6228d7)', sub: '@soomuchschool · 6.1K followers' },
  { key: 'facebook', name: 'Facebook', mono: 'FB', chip: '#1877F2', sub: 'Soomuch Demo School page · 8.4K followers' },
  { key: 'youtube', name: 'YouTube', mono: 'YT', chip: '#FF0000', sub: 'Soomuch Demo School channel' },
  { key: 'whatsapp', name: 'WhatsApp', mono: 'WA', chip: '#25D366', sub: 'Broadcast to parent groups' },
  { key: 'parentApp', name: 'Parent App', mono: 'PA', chip: '#6D5AE6', sub: 'Notifies all parents' },
  { key: 'facultyApp', name: 'Faculty App', mono: 'FA', chip: '#16A34A', sub: 'Shares with staff & faculty' },
];

type SocialHubPlatformKey = 'fb' | 'ig' | 'yt' | 'wa';

const reelSocialHubPlatformKeys: Record<string, SocialHubPlatformKey> = {
  instagram: 'ig',
  facebook: 'fb',
  youtube: 'yt',
  whatsapp: 'wa',
};

const getLocalDateInputValue = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getLocalTimeInputValue = (date = new Date()) =>
  `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;

const getScheduleValidationError = (date: string, time: string) => {
  if (!date || !time) return '';
  const scheduledAt = new Date(`${date}T${time}:00`);
  if (Number.isNaN(scheduledAt.getTime())) return 'Please select a valid date and time.';
  return scheduledAt.getTime() <= Date.now()
    ? 'Please select a future date and time.'
    : '';
};

const formatScheduledDateTime = (date?: string, time?: string) => {
  const [year, month, day] = (date || '').split('-');
  const formattedDate = year && month && day ? `${day}/${month}/${year}` : date || '';
  const [hourValue, minute = '00'] = (time || '').split(':');
  const hour = Number(hourValue);
  const formattedTime = time && Number.isInteger(hour) && hour >= 0 && hour <= 23
    ? `${String(hour % 12 || 12).padStart(2, '0')}:${minute} ${hour >= 12 ? 'PM' : 'AM'}`
    : time || '';
  return [formattedDate, formattedTime].filter(Boolean).join(' · ');
};

const getReelClientStatuses = (): Record<string, ReelClientStatus> => {
  try {
    return JSON.parse(window.localStorage.getItem(reelClientStatusStorageKey) || '{}');
  } catch {
    return {};
  }
};

const saveReelClientStatus = (reelId: number, status: ReelClientStatus) => {
  const statuses = getReelClientStatuses();
  window.localStorage.setItem(reelClientStatusStorageKey, JSON.stringify({ ...statuses, [reelId]: { ...statuses[reelId], ...status } }));
};

type SocialPostPayload = {
  MEDIA_TYPE: 'PHOTO' | 'VIDEO' | 'REEL';
  TITLE: string;
  DESCRIPTION?: string;
  MEDIA: string[];
  BRANCH_ID?: number | string;
};

type ReelFormValues = {
  reelId: number;
  title: string;
  description: string;
  mediaUrl: string;
  mediaFileName: string;
  mediaUrls?: string[];
  mediaFileNames?: string[];
  category: string;
  subCategory: string;
  style: string;
  hashtags?: string;
};

const emptyReelForm: ReelFormValues = {
  reelId: 0,
  title: '',
  description: '',
  mediaUrl: '',
  mediaFileName: '',
  category: 'Campus & Infrastructure',
  subCategory: '',
  style: 'cinematic',
  hashtags: '',
};

const reelCategories = [
  { label: 'Campus & Infrastructure', icon: '🏫' },
  { label: 'Testimonials & Stories', icon: '💬' },
  { label: 'Admissions & Information', icon: '📋' },
  { label: 'Events & Celebrations', icon: '🎉' },
  { label: 'Achievements & Results', icon: '🏆' },
  { label: 'Academics & Learning', icon: '📚' },
  { label: 'Educational & Value Content', icon: '💡' },
  { label: 'Extracurricular & Skill Development', icon: '🎨' },
  { label: 'Community & Social Initiatives', icon: '🤝' },
  { label: 'Sports & Competitions', icon: '⚽' },
];

type ReelCategoryDesign = {
  key: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  subs: string[];
  styles: string[];
};

const reelDesignCategories: ReelCategoryDesign[] = [
  { key: 'campus', label: 'Campus & Infrastructure', icon: School, subs: ['Campus Tour', 'Classrooms', 'Smart Classrooms', 'Science Labs', 'Computer Labs', 'Library', 'Sports Facilities', 'Auditorium', 'Hostel', 'Cafeteria', 'Playground', 'Transport', 'Safety & Security', 'Green Campus'], styles: ['Campus Tour', 'Cinematic', 'Slow Motion', 'Dynamic Montage', 'Minimal Clean', 'Storytelling'] },
  { key: 'testimonials', label: 'Testimonials & Stories', icon: MessageCircle, subs: ['Parent Testimonials', 'Student Testimonials', 'Alumni Stories', 'Teacher Testimonials', "Principal's Message", 'Success Stories', 'Parent Feedback', 'Staff Experiences'], styles: ['Storytelling', 'Cinematic', 'Highlight Reel', 'Inspirational / Motivational', 'Minimal Clean'] },
  { key: 'admissions', label: 'Admissions & Information', icon: ClipboardList, subs: ['Admission Open', 'Admission Process', 'Why Choose Our School', 'Fee Information', 'Scholarships', 'Eligibility Criteria', 'Important Dates', 'School Highlights', 'Contact Information', 'FAQ'], styles: ['Storytelling', 'Cinematic', 'Minimal Clean', 'AI Auto Highlights', 'Inspirational / Motivational'] },
  { key: 'events', label: 'Events & Celebrations', icon: PartyPopper, subs: ['Annual Day', 'Sports Day', 'Cultural Fest', 'Independence Day', 'Republic Day', "Teachers' Day", "Children's Day", 'Graduation Day', 'Festivals', 'Birthday Celebrations', 'Field Trips', 'Competitions', 'Workshops', 'Seminars'], styles: ['Event Recap', 'Celebration Style', 'Beat Sync', 'Dynamic Montage', 'Highlight Reel', 'Slow Motion'] },
  { key: 'achievements', label: 'Achievements & Results', icon: Trophy, subs: ['Academic Toppers', 'Board Results', 'Olympiad Winners', 'Sports Achievements', 'Competition Winners', 'Awards & Recognition', 'School Rankings', 'Student Success Stories', 'Teacher Achievements'], styles: ['Achievement Showcase', 'Highlight Reel', 'Cinematic', 'Inspirational / Motivational', 'AI Auto Highlights'] },
  { key: 'academics', label: 'Academics & Learning', icon: BookOpen, subs: ['Classroom Activities', 'Smart Learning', 'STEM Education', 'Project-Based Learning', 'Experiments', 'Language Learning', 'Reading Programs', 'Exam Preparation', 'Homework Activities', 'AI Learning Tools'], styles: ['Storytelling', 'Minimal Clean', 'Beat Sync', 'AI Auto Highlights', 'Inspirational / Motivational'] },
  { key: 'educational', label: 'Educational & Value Content', icon: Lightbulb, subs: ['Study Tips', 'Career Guidance', 'Parenting Tips', 'Moral Values', 'Health & Hygiene', 'Digital Safety', 'Environmental Awareness', 'Motivational Quotes', 'Learning Hacks', 'General Knowledge'], styles: ['Storytelling', 'Minimal Clean', 'Cinematic', 'AI Auto Highlights', 'Inspirational / Motivational'] },
  { key: 'extracurricular', label: 'Extracurricular & Skill Development', icon: Palette, subs: ['Dance', 'Music', 'Art & Craft', 'Drama', 'Robotics', 'Coding', 'Public Speaking', 'Debate', 'Yoga', 'Chess', 'Personality Development', 'Leadership Programs', 'Clubs & Societies'], styles: ['Dynamic Montage', 'Beat Sync', 'Highlight Reel', 'Storytelling', 'Inspirational / Motivational'] },
  { key: 'community', label: 'Community & Social Initiatives', icon: HeartHandshake, subs: ['Social Service', 'Tree Plantation', 'Blood Donation', 'Charity Drives', 'Swachh Bharat Activities', 'Awareness Campaigns', 'Community Outreach', 'NGO Collaborations', 'Eco Club Activities'], styles: ['Storytelling', 'Event Recap', 'Cinematic', 'AI Auto Highlights', 'Inspirational / Motivational'] },
  { key: 'sports', label: 'Sports & Competitions', icon: Goal, subs: ['Football', 'Cricket', 'Basketball', 'Volleyball', 'Athletics', 'Indoor Games', 'Martial Arts', 'Yoga Competitions', 'Inter-School Competitions', 'Annual Sports Meet', 'Practice Sessions', 'Winners Highlights'], styles: ['Sports Highlight', 'Slow Motion', 'Dynamic Montage', 'Beat Sync', 'Highlight Reel', 'Cinematic'] },
  { key: 'graduation', label: 'Graduation & Milestones', icon: GraduationCap, subs: ['Graduation Ceremony', 'Farewell', 'Convocation', 'Student Journey', 'Memory Lane', 'Batch Highlights', 'Alumni Welcome', 'Certificate Distribution'], styles: ['Graduation Style', 'Celebration Style', 'Cinematic', 'Highlight Reel', 'Storytelling', 'Inspirational / Motivational'] },
];

const reelStyleOptions = [
  { label: 'Cinematic', value: 'cinematic' },
  { label: 'Explainer', value: 'explainer' },
  { label: 'Typography', value: 'typography' },
  { label: 'Promotional', value: 'promotional' },
  { label: 'Feature Highlight', value: 'feature-highlight' },
  { label: 'Story Telling', value: 'story telling' },
];

const reelFormChipClassName =
  'h-[4.2vh] rounded-full border px-[1vw] text-[1.45vh] font-semibold transition-colors';
const emptyReelPlatforms = {
  facebook: false,
  instagram: false,
  youtube: false,
  website: false,
  whatsapp: false,
};

const reelPlatformLabels: Record<'facebook' | 'instagram' | 'youtube', string> =
{
  facebook: 'Facebook',
  instagram: 'Instagram',
  youtube: 'YouTube',
};

const formatReelDateTime = (dateValue: string) => {
  if (!dateValue) {
    return { date: '-', time: '-' };
  }

  const parsed = new Date(dateValue);
  if (Number.isNaN(parsed.getTime())) {
    return { date: dateValue, time: '-' };
  }

  return {
    date: parsed.toLocaleDateString('en-GB'),
    time: parsed.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
    }),
  };
};

const getReelId = (item: any) =>
  Number(
    item?.id ||
    item?.ID ||
    item?.reel_id ||
    item?.REEL_ID ||
    item?.branch_reel_id ||
    item?.BRANCH_REEL_ID ||
    0
  );

const hasResponseKey = (item: any, key: string) =>
  Object.prototype.hasOwnProperty.call(item || {}, key);

const normalizeReelImages = (item: any): string[] => {
  const imageValue =
    item?.images ||
    item?.IMAGES ||
    item?.image_urls ||
    item?.IMAGE_URLS ||
    item?.image_url ||
    item?.IMAGE_URL ||
    '';

  if (Array.isArray(imageValue)) {
    return imageValue.filter(Boolean);
  }

  if (typeof imageValue === 'string') {
    try {
      const parsed = JSON.parse(imageValue);
      if (Array.isArray(parsed)) {
        return parsed.filter(Boolean);
      }
    } catch (error) {
      return imageValue ? [imageValue] : [];
    }
  }

  return [];
};

const mapReelRow = (item: any): ReelItem => {
  const dateTime = formatReelDateTime(
    item?.updated_at ||
    item?.UPDATED_AT ||
    item?.created_at ||
    item?.createdAt ||
    item?.date ||
    ''
  );
  const images = normalizeReelImages(item);
  const videoStatus = String(
    item?.video_status || item?.VIDEO_STATUS || ''
  ).toUpperCase();
  const facebook = Boolean(item?.facebook ?? item?.FACEBOOK);
  const instagram = Boolean(item?.instagram ?? item?.INSTAGRAM);
  const youtube = Boolean(item?.youtube ?? item?.YOUTUBE);
  const whatsapp = Boolean(item?.whatsapp ?? item?.WHATSAPP);
  const parentApp = Boolean(item?.parent_app ?? item?.PARENT_APP);
  const facultyApp = Boolean(item?.faculty_app ?? item?.FACULTY_APP);
  const isPublished =
    facebook || instagram || youtube || whatsapp || parentApp || facultyApp;
  const rawHashtags = item?.hashtags || item?.HASHTAGS || [];
  const hashtags = Array.isArray(rawHashtags)
    ? rawHashtags.join(' ')
    : String(rawHashtags || '');
  const status =
    videoStatus === 'PROCESSING'
      ? 'generating'
      : isPublished
        ? 'posted'
        : 'generated';

  return {
    id: getReelId(item),
    title: item?.title || item?.TITLE || 'Fun Activities at School',
    description: item?.description || item?.DESCRIPTION || '',
    category: item?.category || item?.CATEGORY || '',
    subCategory:
      item?.sub_category || item?.SUB_CATEGORY || item?.subcategory || '',
    style: item?.style || item?.STYLE || '',
    images,
    videoId: item?.video_id || item?.VIDEO_ID || '',
    videoStatus,
    videoUrl:
      item?.video_url ||
      item?.VIDEO_URL ||
      item?.reel_url ||
      item?.REEL_URL ||
      '',
    thumbnail:
      item?.thumbnail ||
      item?.THUMBNAIL ||
      item?.thumbnail_url ||
      item?.THUMBNAIL_URL ||
      images[0] ||
      imageBg,
    date: dateTime.date,
    time: dateTime.time,
    views: Number(
      item?.views || item?.VIEWS || item?.view_count || item?.VIEW_COUNT || 0
    ),
    school: item?.school || item?.SCHOOL || 'DemoSchool',
    facebook,
    instagram,
    youtube,
    whatsapp,
    parentApp,
    facultyApp,
    hashtags,
    availablePlatforms: [
      ...(hasResponseKey(item, 'facebook') || hasResponseKey(item, 'FACEBOOK')
        ? ['facebook' as const]
        : []),
      ...(hasResponseKey(item, 'instagram') || hasResponseKey(item, 'INSTAGRAM')
        ? ['instagram' as const]
        : []),
      ...(hasResponseKey(item, 'youtube') || hasResponseKey(item, 'YOUTUBE')
        ? ['youtube' as const]
        : []),
    ],
    status,
    raw: item,
  };
};

const getReelList = (response: any) =>
  response?.data?.data?.Reels ||
  response?.data?.data?.REELS ||
  response?.data?.data?.reels ||
  getResponseList(response);

const getReelTotal = (response: any) =>
  Number(
    response?.data?.data?.Total ||
    response?.data?.data?.TOTAL ||
    response?.data?.data?.total ||
    getReelList(response).length
  );

const getReelDetails = (response: any) => response?.data?.data || {};

const openSocialPostUrl = (response: any) => {
  const url = response?.data?.data?.url;

  if (typeof url === 'string' && url.trim()) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
};

const buildFacebookReelPayload = (reel: ReelItem): SocialPostPayload => {
  const raw = reel.raw || {};
  const title = raw?.title || raw?.TITLE || reel.title;
  const description = raw?.description || raw?.DESCRIPTION || reel.description;
  const videoUrl =
    raw?.video_url ||
    raw?.VIDEO_URL ||
    raw?.reel_url ||
    raw?.REEL_URL ||
    reel.videoUrl;
  const media = [
    ...(typeof videoUrl === 'string' && videoUrl ? [videoUrl] : []),
    ...normalizeReelImages(raw),
  ];

  return {
    MEDIA_TYPE: 'REEL',
    TITLE: title,
    DESCRIPTION: description,
    MEDIA: media.length ? media : reel.images,
  };
};

const PlayButton = () => (
  <DMText
    as="span"
    className="flex h-[7.8vh] w-[7.8vh] items-center justify-center rounded-full bg-black/70 text-white"
  >
    <Play
      className="ml-[0.3vw] h-[4.2vh] w-[4.2vh] fill-white"
      strokeWidth={1.6}
    />
  </DMText>
);

const GeneratingPreview = () => (
  <DMDiv className="relative aspect-[1.72] w-full overflow-hidden rounded-[8px] border border-gray-300 bg-gray-100">
    <DMImage
      src={sampleImg}
      alt=""
      className="h-full w-full object-cover opacity-20 blur-[1px]"
    />
    <DMDiv className="absolute inset-0 flex flex-col items-center justify-center bg-white/45 text-center">
      <DMDiv className="mb-[1.8vh] flex h-[5vh] w-[5vh] items-center justify-center rounded-[8px] border border-primary-main/30 text-primary-main">
        <Play className="h-[3vh] w-[3vh]" strokeWidth={1.8} />
      </DMDiv>
      <DMDiv className="h-[0.35vh] w-[8vw] overflow-hidden rounded-full bg-white">
        <DMDiv className="h-full w-[42%] rounded-full bg-primary-main" />
      </DMDiv>
      <DMText className="mt-[1.6vh] text-[1.75vh] font-semibold leading-tight text-gray-500">
        Reel is being generated
        <DMBr />
        Please check back later
      </DMText>
    </DMDiv>
  </DMDiv>
);

const ReelCard = ({
  item,
  activeMenuId,
  setActiveMenuId,
  onPlay,
  onShare,
  onDelete,
}: {
  item: ReelItem;
  activeMenuId: number | null;
  setActiveMenuId: (id: number | null) => void;
  onPlay: (item: ReelItem) => void;
  onShare: (item: ReelItem) => void;
  onDelete: (item: ReelItem) => void;
}) => {
  const menuRef = React.useRef<HTMLDivElement>(null);
  const isMenuOpen = activeMenuId === item.id;
  const isPublished = isReelPublished(item);

  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setActiveMenuId(null);
      }
    }

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen, setActiveMenuId]);

  return (
    <DMDiv as="article" className="relative min-w-0">
      {item.status === 'generating' ? (
        <GeneratingPreview />
      ) : (
        <DMButton
          type="button"
          onClick={() => onPlay(item)}
          className="relative block aspect-[1.72] w-full overflow-hidden rounded-[8px] bg-gray-200 text-left"
          aria-label={`Open ${item.title}`}
        >
          {item.videoUrl ? (
            <DMVideo
              src={`${item.videoUrl}#t=0.1`}
              muted
              preload="metadata"
              playsInline
              className="h-full w-full object-cover"
            />
          ) : (
            <DMImage
              src={item.thumbnail}
              alt={item.title}
              className="h-full w-full object-cover"
            />
          )}
          <DMDiv className="absolute inset-0 flex items-center justify-center">
            <PlayButton />
          </DMDiv>
        </DMButton>
      )}

      <DMDiv className="mt-[2vh] flex items-start justify-between gap-[1vw]">
        <DMDiv className="min-w-0 flex-1">
          <DMText
            as="h2"
            className="truncate text-[2.25vh] font-bold leading-tight text-gray-950"
          >
            {item.title}
          </DMText>
          <DMText className="mt-[1.2vh] truncate text-[1.55vh] font-medium leading-tight text-gray-500">
            {item.date} | {item.time} |{' '}
            <Eye className="inline h-[1.7vh] w-[1.7vh]" /> {item.views} |{' '}
            {item.school}
          </DMText>
        </DMDiv>
        <DMDiv className="relative shrink-0" ref={menuRef}>
          <DMButton
            type="button"
            onClick={() => setActiveMenuId(isMenuOpen ? null : item.id)}
            className="flex h-[3.2vh] w-[2vw] items-center justify-center text-gray-950"
            aria-label={`Open actions for ${item.title}`}
          >
            <MoreVertical className="h-[2.8vh] w-[2.8vh]" strokeWidth={2.5} />
          </DMButton>
          {isMenuOpen && (
            <DMDiv className="absolute bottom-full right-0 z-20 mb-1 w-[9.4vw] min-w-[120px] overflow-hidden rounded-[6px] border border-gray-300 bg-white py-[0.6vh] shadow-[0_8px_18px_rgba(0,0,0,0.16)]">
              <DMButton
                type="button"
                onClick={() => onShare(item)}
                className="flex w-full items-center gap-[0.7vw] px-[0.9vw] py-[1vh] text-left text-[1.65vh] font-medium text-gray-950 hover:bg-gray-50"
              >
                <Share2 className="h-[1.8vh] w-[1.8vh]" /> Publish
              </DMButton>
              <DMButton
                type="button"
                onClick={() => {
                  if (isPublished) {
                    return;
                  }

                  onDelete(item);
                }}
                aria-disabled={isPublished}
                title={
                  isPublished
                    ? 'This reel is already published. Published reels cannot be deleted.'
                    : 'Delete'
                }
                className={`flex w-full items-center gap-[0.7vw] px-[0.9vw] py-[1vh] text-left text-[1.65vh] font-medium ${isPublished
                    ? 'cursor-not-allowed text-gray-400'
                    : 'text-red-600 hover:bg-red-50'
                  }`}
              >
                <Trash2 className="h-[1.8vh] w-[1.8vh]" /> Delete
              </DMButton>
            </DMDiv>
          )}
        </DMDiv>
      </DMDiv>
    </DMDiv>
  );
};

const ReelCardSkeleton = () => (
  <DMDiv className="min-w-0 animate-pulse" aria-hidden="true">
    <DMDiv className="aspect-[1.72] rounded-[8px] bg-gray-200" />
    <DMDiv className="mt-[2vh] flex items-start justify-between gap-[1vw]">
      <DMDiv className="min-w-0 flex-1">
        <DMDiv className="h-[2.4vh] w-[75%] rounded bg-gray-200" />
        <DMDiv className="mt-[1.2vh] h-[1.6vh] w-[58%] rounded bg-gray-200" />
      </DMDiv>
    </DMDiv>
  </DMDiv>
);

const ReelPreviewSkeletonBlock = ({ className }: { className: string }) => (
  <DMDiv
    className={`animate-pulse rounded-[8px] bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100 ${className}`}
    aria-hidden="true"
  />
);

const ReelListPreviewSkeleton = () => (
  <DMDiv
    as="main"
    className="flex h-full min-h-0 flex-col overflow-hidden bg-[#F7F8FB] px-[2.2vw] py-[2.4vh] font-inter text-gray-950"
    aria-busy="true"
  >
    <DigitalMarketingBreadcrumb
      className="mb-[1.8vh] shrink-0"
      items={[
        { label: 'Reels', path: REELS_BASE_PATH },
        { label: 'Preview Reel' },
      ]}
    />
    <DMDiv className="flex min-h-0 flex-1 w-full gap-[2vw]">
      <DMDiv
        as="section"
        className="flex min-h-0 flex-[0.95] items-center justify-center overflow-hidden rounded-[8px] border border-gray-200 bg-[#111827] p-[2.2vh] shadow-[0_18px_45px_rgba(15,23,42,0.16)]"
      >
        <DMDiv className="aspect-[9/16] h-full max-h-full max-w-full overflow-hidden rounded-[8px] bg-black p-[1vh] shadow-[0_16px_35px_rgba(0,0,0,0.28)]">
          <DMDiv
            className="h-full w-full animate-pulse rounded-[6px] bg-gradient-to-b from-gray-800 via-gray-700 to-gray-900"
            aria-hidden="true"
          />
        </DMDiv>
      </DMDiv>

      <DMDiv
        as="section"
        className="flex min-h-0 flex-1 flex-col rounded-[8px] border border-gray-200 bg-white p-[2.4vh] shadow-[0_12px_30px_rgba(15,23,42,0.08)]"
      >
        <DMDiv className="flex shrink-0 items-center justify-between gap-[1vw]">
          <ReelPreviewSkeletonBlock className="h-[4.6vh] w-[7.5vw] rounded-full" />
          <ReelPreviewSkeletonBlock className="h-[3.8vh] w-[7vw] rounded-full" />
        </DMDiv>

        <DMDiv className="mt-[2vh] shrink-0">
          <ReelPreviewSkeletonBlock className="h-[1.6vh] w-[8vw]" />
          <ReelPreviewSkeletonBlock className="mt-[1.1vh] h-[3.4vh] w-[75%]" />
          <ReelPreviewSkeletonBlock className="mt-[0.9vh] h-[3.4vh] w-[52%]" />
        </DMDiv>

        <DMDiv className="mt-[1.8vh] grid shrink-0 grid-cols-3 gap-[0.8vw]">
          {[0, 1, 2].map(item => (
            <DMDiv
              key={item}
              className="rounded-[8px] border border-gray-200 bg-gray-50 px-[1vw] py-[1.3vh]"
            >
              <ReelPreviewSkeletonBlock className="h-[1.4vh] w-[45%]" />
              <ReelPreviewSkeletonBlock className="mt-[0.8vh] h-[2vh] w-[72%]" />
            </DMDiv>
          ))}
        </DMDiv>

        <DMDiv className="mt-[1.8vh] shrink-0 rounded-[8px] border border-gray-200 bg-white px-[1vw] py-[1.4vh]">
          <ReelPreviewSkeletonBlock className="h-[1.4vh] w-[10%]" />
          <ReelPreviewSkeletonBlock className="mt-[0.8vh] h-[2.1vh] w-[64%]" />
        </DMDiv>

        <DMDiv className="mt-[1.8vh] min-h-0 flex-1 rounded-[8px] border border-gray-200 bg-gray-50 p-[1.2vw]">
          <ReelPreviewSkeletonBlock className="h-[1.4vh] w-[16%]" />
          <ReelPreviewSkeletonBlock className="mt-[1.2vh] h-[2vh] w-full" />
          <ReelPreviewSkeletonBlock className="mt-[1vh] h-[2vh] w-[92%]" />
          <ReelPreviewSkeletonBlock className="mt-[1vh] h-[2vh] w-[84%]" />
          <ReelPreviewSkeletonBlock className="mt-[1vh] h-[2vh] w-[68%]" />
        </DMDiv>
      </DMDiv>
    </DMDiv>
  </DMDiv>
);

const ReelPlayerModal = ({
  item,
  onClose,
}: {
  item: ReelItem | null;
  onClose: () => void;
}) => {
  if (!item) {
    return null;
  }

  return (
    <DMDiv className="fixed inset-0 z-[10002] flex items-center justify-center px-[3vw] py-[4vh]">
      <DMButton
        type="button"
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-label="Close reel player backdrop"
      />
      <DMDiv className="relative z-10 w-[28vw] min-w-[320px] max-w-[460px] overflow-hidden rounded-[10px] bg-black shadow-[0_18px_45px_rgba(0,0,0,0.35)]">
        <DMButton
          type="button"
          onClick={onClose}
          className="absolute right-[1vw] top-[1.2vh] z-10 flex h-[4vh] w-[4vh] items-center justify-center rounded-full bg-black/60 text-white"
          aria-label="Close reel player"
        >
          <X className="h-[2.4vh] w-[2.4vh]" strokeWidth={2.3} />
        </DMButton>
        <DMDiv className="relative aspect-[9/16] bg-gray-900">
          {item.videoUrl ? (
            <DMVideo
              src={item.videoUrl}
              poster={item.thumbnail}
              controls
              className="h-full w-full object-cover"
            />
          ) : (
            <>
              <DMImage
                src={item.thumbnail}
                alt={item.title}
                className="h-full w-full object-cover"
              />
              <DMDiv className="absolute inset-0 flex items-center justify-center">
                <PlayButton />
              </DMDiv>
            </>
          )}
        </DMDiv>
      </DMDiv>
    </DMDiv>
  );
};

const DisabledReelToggle = ({ checked }: { checked: boolean }) => (
  <DMButton
    type="button"
    disabled
    className={`w-[4.4vh] h-[2.4vh] rounded-[2vh] p-[0.3vh] transition-colors duration-200 ease-in-out cursor-not-allowed opacity-60 ${checked ? 'bg-primary-main' : 'bg-gray-200'
      }`}
  >
    <DMDiv
      className={`w-[1.8vh] h-[1.8vh] bg-white rounded-full shadow-sm transform transition-transform duration-200 ease-in-out ${checked ? 'translate-x-[2vh]' : 'translate-x-0'
        }`}
    />
  </DMButton>
);

const ReelPublishModal = ({
  isOpen,
  onClose,
  platforms,
  visiblePlatforms,
  requestPublish,
  isPublishing,
  publishingPlatform,
}: {
  isOpen: boolean;
  onClose: () => void;
  platforms: typeof emptyReelPlatforms;
  visiblePlatforms: Array<'facebook' | 'instagram' | 'youtube'>;
  requestPublish: (key: 'facebook' | 'instagram' | 'youtube') => void;
  isPublishing: boolean;
  publishingPlatform: 'facebook' | 'instagram' | 'youtube' | null;
}) => (
  <Modal isOpen={isOpen} onClose={isPublishing ? () => undefined : onClose}>
    <DMDiv className="text-left">
      <DMText as="h2" className="mb-2 text-2xl font-bold text-gray-900">
        Publish Reel
      </DMText>
      <DMText className="mb-8 text-gray-500">
        Select the social media platforms where you want to publish your reel
      </DMText>

      <DMDiv className="space-y-4">
        {visiblePlatforms.includes('facebook') && (
          <DMDiv className="flex items-center justify-between border-b border-gray-100 pb-4">
            <DMDiv className="flex items-center gap-3">
              <DMDiv className="flex h-8 w-8 items-center justify-center rounded bg-[#1877F2] text-white">
                <Facebook size={18} />
              </DMDiv>
              <DMText as="span" className="font-medium text-gray-900">
                Share on Facebook
              </DMText>
            </DMDiv>
            {platforms.facebook || isPublishing ? (
              <DMDiv className="flex items-center gap-3">
                {publishingPlatform === 'facebook' && (
                  <DMText
                    as="span"
                    className="text-sm font-medium text-primary-main"
                  >
                    Posting...
                  </DMText>
                )}
                <DisabledReelToggle checked={platforms.facebook} />
              </DMDiv>
            ) : (
              <Toggle
                checked={platforms.facebook}
                onChange={() => requestPublish('facebook')}
              />
            )}
          </DMDiv>
        )}

        {visiblePlatforms.includes('instagram') && (
          <DMDiv className="flex items-center justify-between border-b border-gray-100 pb-4">
            <DMDiv className="flex items-center gap-3">
              <DMDiv className="flex h-8 w-8 items-center justify-center rounded bg-gradient-to-tr from-yellow-400 via-red-500 to-purple-500 text-white">
                <Instagram size={18} />
              </DMDiv>
              <DMText as="span" className="font-medium text-gray-900">
                Share on Instagram
              </DMText>
            </DMDiv>
            {platforms.instagram || isPublishing ? (
              <DMDiv className="flex items-center gap-3">
                {publishingPlatform === 'instagram' && (
                  <DMText
                    as="span"
                    className="text-sm font-medium text-primary-main"
                  >
                    Posting...
                  </DMText>
                )}
                <DisabledReelToggle checked={platforms.instagram} />
              </DMDiv>
            ) : (
              <Toggle
                checked={platforms.instagram}
                onChange={() => requestPublish('instagram')}
              />
            )}
          </DMDiv>
        )}

        {visiblePlatforms.includes('youtube') && (
          <DMDiv className="flex items-center justify-between border-b border-gray-100 pb-4">
            <DMDiv className="flex items-center gap-3">
              <DMDiv className="flex h-8 w-8 items-center justify-center rounded bg-[#FF0000] text-white">
                <Youtube size={18} />
              </DMDiv>
              <DMText as="span" className="font-medium text-gray-900">
                Share on YouTube
              </DMText>
            </DMDiv>
            {platforms.youtube || isPublishing ? (
              <DMDiv className="flex items-center gap-3">
                {publishingPlatform === 'youtube' && (
                  <DMText
                    as="span"
                    className="text-sm font-medium text-primary-main"
                  >
                    Posting...
                  </DMText>
                )}
                <DisabledReelToggle checked={platforms.youtube} />
              </DMDiv>
            ) : (
              <Toggle
                checked={platforms.youtube}
                onChange={() => requestPublish('youtube')}
              />
            )}
          </DMDiv>
        )}
      </DMDiv>

      {isPublishing && (
        <DMDiv className="mt-6 flex items-center gap-3 rounded-lg bg-primary-main/10 px-4 py-3 text-sm font-medium text-primary-main">
          <DMText
            as="span"
            className="h-4 w-4 animate-spin rounded-full border-2 border-primary-main/30 border-t-primary-main"
          />
          <DMText as="span">
            Publishing reel
            {publishingPlatform
              ? ` on ${reelPlatformLabels[publishingPlatform]}`
              : ''}
            . Please wait...
          </DMText>
        </DMDiv>
      )}
    </DMDiv>
  </Modal>
);

function ReelsDashboard() {
  const navigate = useNavigate();
  const location = useLocation();
  const reelsRefreshRequest = location.state?.refreshReels;
  const authData = useAuth();
  const [searchQuery, setSearchQuery] = React.useState('');
  const debouncedSearchQuery = useDebounce(searchQuery, 900);
  const [reelFilter, setReelFilter] = React.useState<ReelFilter>('both');
  const [isLoadingReels, setIsLoadingReels] = React.useState(true);
  const [reelRows, setReelRows] = React.useState<ReelItem[]>([]);
  const [totalReels, setTotalReels] = React.useState(0);
  const [pageIndex, setPageIndex] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(10);
  const [activeMenuId, setActiveMenuId] = React.useState<number | null>(null);
  const [selectedReel, setSelectedReel] = React.useState<ReelItem | null>(null);
  const [reelToDelete, setReelToDelete] = React.useState<ReelItem | null>(null);
  const [reelToPublish, setReelToPublish] = React.useState<ReelItem | null>(
    null
  );
  const [isDeletingReel, setIsDeletingReel] = React.useState(false);
  const [isPublishingReel, setIsPublishingReel] = React.useState(false);
  const [publishingPlatform, setPublishingPlatform] = React.useState<
    'facebook' | 'instagram' | 'youtube' | null
  >(null);
  const [platforms, setPlatforms] = React.useState(emptyReelPlatforms);

  const fetchReels = React.useCallback(async () => {
    setIsLoadingReels(true);

    try {
      const response = await reelsApi.list(pageIndex, pageSize);
      setReelRows(getReelList(response).map(mapReelRow));
      setTotalReels(getReelTotal(response));
    } catch (error) {
      setReelRows([]);
      setTotalReels(0);
    } finally {
      setIsLoadingReels(false);
    }
  }, [pageIndex, pageSize]);

  React.useEffect(() => {
    fetchReels();
  }, [fetchReels, reelsRefreshRequest]);

  React.useEffect(() => {
    setPageIndex(1);
  }, [debouncedSearchQuery, reelFilter]);

  React.useEffect(() => {
    if (location.state?.showGeneratingReel) {
      setReelRows(current => [
        {
          id: Date.now(),
          title: location.state?.title || 'Fun Activities at School',
          description: '',
          videoUrl: '',
          thumbnail: sampleImg,
          category: '',
          subCategory: '',
          style: '',
          images: [],
          videoId: '',
          videoStatus: 'PROCESSING',
          date: '00/00/2025',
          time: '00:00 PM',
          views: 0,
          school: 'DemoSchool',
          facebook: false,
          instagram: false,
          youtube: false,
          whatsapp: false,
          parentApp: false,
          facultyApp: false,
          hashtags: '',
          availablePlatforms: ['facebook', 'instagram', 'youtube'],
          status: 'generating',
          raw: {},
        },
        ...current,
      ]);
      setTotalReels(current => current + 1);
      setReelFilter('generated');
      navigate(REELS_BASE_PATH, { replace: true });
    }
  }, [location.state, navigate]);

  React.useEffect(() => {
    const pending = getPendingReelGeneration();
    if (!pending?.videoId) return undefined;

    let isActive = true;
    let intervalId: number | undefined;
    const checkStatus = async () => {
      try {
        const result = await reelsApi.getStatus(pending.videoId);
        if (!isActive) return;
        const status = String(result?.status || '').toUpperCase();

        setReelRows(current =>
          current.map(item =>
            item.id === pending.reelId || item.videoId === pending.videoId
              ? {
                ...item,
                videoId: pending.videoId,
                videoStatus: status,
                videoUrl: result?.video_url || item.videoUrl,
                status: status === 'PROCESSING' ? 'generating' : 'generated',
              }
              : item
          )
        );

        if (status === 'COMPLETED') {
          clearPendingReelGeneration();
          if (intervalId) window.clearInterval(intervalId);
          showGlobalModal('Your reel is ready');
          await fetchReels();
        } else if (status === 'FAILED') {
          clearPendingReelGeneration();
          if (intervalId) window.clearInterval(intervalId);
          showGlobalModal('Reel generation failed. Please try again.', 'error');
          await fetchReels();
        }
      } catch {
        // The status endpoint is a documented fallback; retry on the next tick.
      }
    };

    checkStatus();
    intervalId = window.setInterval(checkStatus, 10000);
    return () => {
      isActive = false;
      window.clearInterval(intervalId);
    };
  }, [fetchReels]);

  const searchedReels = React.useMemo(() => {
    const normalizedSearch = debouncedSearchQuery.trim().toLowerCase();

    if (!normalizedSearch) {
      return reelRows;
    }

    return reelRows.filter(
      item =>
        item.title.toLowerCase().includes(normalizedSearch) ||
        item.school.toLowerCase().includes(normalizedSearch) ||
        item.date.toLowerCase().includes(normalizedSearch) ||
        item.description.toLowerCase().includes(normalizedSearch)
    );
  }, [debouncedSearchQuery, reelRows]);

  const visibleReels = React.useMemo(() => {
    if (reelFilter === 'generated') {
      return searchedReels.filter(item => item.status !== 'posted');
    }

    if (reelFilter === 'posted') {
      return searchedReels.filter(item => item.status === 'posted');
    }

    return searchedReels;
  }, [reelFilter, searchedReels]);

  const hasAnyReels = reelRows.length > 0;

  const handleDelete = (item: ReelItem) => {
    if (isReelPublished(item)) {
      return;
    }
    setActiveMenuId(null);
    setReelToDelete(item);
  };

  const handleCancelDelete = () => {
    if (isDeletingReel) {
      return;
    }

    setReelToDelete(null);
  };

  const handleConfirmDelete = async () => {
    if (!reelToDelete || isReelPublished(reelToDelete)) {
      return;
    }

    const deleteId = reelToDelete.id;
    setIsDeletingReel(true);
    setReelRows(current => current.filter(reel => reel.id !== deleteId));

    try {
      await reelsApi.delete(deleteId);
      showGlobalModal('Reel deleted successfully');
      setTotalReels(current => Math.max(0, current - 1));
      if (reelRows.length === 1 && pageIndex > 1) {
        setPageIndex(current => current - 1);
      } else {
        await fetchReels();
      }
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to delete reel'),
        'error'
      );
      fetchReels();
    } finally {
      setIsDeletingReel(false);
      setReelToDelete(null);
    }
  };

  const handleShare = (item: ReelItem) => {
    setActiveMenuId(null);
    if (item.status === 'generating') {
      showGlobalModal(
        'Reel is still being generated. Please check back later.',
        'warning'
      );
      return;
    }
    setPlatforms({
      ...emptyReelPlatforms,
      facebook: item.facebook,
      instagram: item.instagram,
      youtube: item.youtube,
    });
    setReelToPublish(item);
  };

  const handleClosePublishModal = () => {
    if (isPublishingReel) {
      return;
    }

    setReelToPublish(null);
    setPlatforms(emptyReelPlatforms);
  };

  const publishReelPlatform = async (
    platformKey: 'facebook' | 'instagram' | 'youtube'
  ) => {
    if (!reelToPublish?.id) {
      showGlobalModal('Reel id is missing', 'error');
      return;
    }

    if (!reelToPublish.availablePlatforms.includes(platformKey)) {
      showGlobalModal(
        'This social platform is not available for this reel',
        'warning'
      );
      return;
    }

    setIsPublishingReel(true);
    setPublishingPlatform(platformKey);

    let fallbackErrorMessage = 'Unable to update reel platform';

    try {
      const platform = platformKey.toUpperCase();

      if (
        platformKey === 'facebook' ||
        platformKey === 'instagram' ||
        platformKey === 'youtube'
      ) {
        const platformLabel = reelPlatformLabels[platformKey];

        if (platformKey === 'youtube' && !authData?.BRANCH_ID) {
          showGlobalModal('Branch id is missing', 'error');
          return;
        }

        const socialReelPayload = {
          ...buildFacebookReelPayload(reelToPublish),
          ...(platformKey === 'youtube'
            ? { BRANCH_ID: authData?.BRANCH_ID as number | string }
            : {}),
        };

        if (!socialReelPayload.MEDIA.length) {
          showGlobalModal('Reel media is missing', 'error');
          return;
        }

        fallbackErrorMessage = `Unable to publish reel on ${platformLabel}`;
        const socialPostResponse = await httpclient().post(
          `/comm/${platformKey}/post`,
          socialReelPayload
        );
        openSocialPostUrl(socialPostResponse);
        fallbackErrorMessage = 'Unable to update reel platform';
      }

      const updatedReel = await reelsApi.updatePlatform(
        reelToPublish.id,
        platform
      );

      setReelRows(current =>
        current.map(item =>
          item.id === reelToPublish.id
            ? {
              ...item,
              facebook: Boolean(
                updatedReel.facebook ??
                (platformKey === 'facebook' ? true : item.facebook)
              ),
              instagram: Boolean(
                updatedReel.instagram ??
                (platformKey === 'instagram' ? true : item.instagram)
              ),
              youtube: Boolean(
                updatedReel.youtube ??
                (platformKey === 'youtube' ? true : item.youtube)
              ),
              status: 'posted',
            }
            : item
        )
      );

      setReelToPublish(current =>
        current
          ? {
            ...current,
            facebook: Boolean(
              updatedReel.facebook ??
              (platformKey === 'facebook' ? true : current.facebook)
            ),
            instagram: Boolean(
              updatedReel.instagram ??
              (platformKey === 'instagram' ? true : current.instagram)
            ),
            youtube: Boolean(
              updatedReel.youtube ??
              (platformKey === 'youtube' ? true : current.youtube)
            ),
            status: 'posted',
          }
          : current
      );
      setPlatforms(current => ({ ...current, [platformKey]: true }));
      showGlobalModal(
        `Reel published on ${platformKey === 'facebook'
          ? 'Facebook'
          : platformKey === 'instagram'
            ? 'Instagram'
            : 'YouTube'
        } successfully`
      );
    } catch (error: any) {
      showGlobalModal(getApiErrorMessage(error, fallbackErrorMessage), 'error');
    } finally {
      setIsPublishingReel(false);
      setPublishingPlatform(null);
    }
  };

  const requestPublishPlatform = (
    platformKey: 'facebook' | 'instagram' | 'youtube'
  ) => {
    if (platforms[platformKey]) {
      return;
    }

    const platformLabel =
      platformKey === 'facebook'
        ? 'Facebook'
        : platformKey === 'instagram'
          ? 'Instagram'
          : 'YouTube';
    showGlobalModal(
      `Are you sure you want to publish this reel on ${platformLabel}? Once published, the reel cannot be deleted.`,
      'warning',
      {
        showConfirm: true,
        onConfirm: () => publishReelPlatform(platformKey),
        onCancel: () => undefined,
        confimLabel: 'Publish',
        cancelLabel: 'Cancel',
      }
    );
  };

  const useStandaloneReelsDesign = true;
  if (useStandaloneReelsDesign) {
    return (
      <>
        <DMDiv as="main" className="reel-reference-screen rf-enter flex h-full min-h-0 flex-col overflow-y-auto px-[1.7vw] py-[2vh] font-inter text-[#161328]">
          <DMDiv className="flex shrink-0 items-end justify-between gap-[1vw] pt-[1vh]">
            <DMDiv className="flex items-center gap-[0.85vw]">
              <DMButton type="button" onClick={() => navigate('/digital-marketing/dashboard')} title="Back to Digital Marketing" aria-label="Back to Digital Marketing" className="rf-icon-button text-[#4F46E5]">
                <ChevronLeft className="h-[2vh] w-[2vh]" strokeWidth={2.2} />
              </DMButton>
              <DMDiv>
                <DMText as="h1" className="text-[3vh] font-semibold leading-none tracking-[-0.02em] text-[#161328]">Reels</DMText>
                <DMText className="mt-[0.55vh] text-[1.4vh] text-[#9B9AB3]">AI-generated reels, ready to preview and share across every channel</DMText>
              </DMDiv>
            </DMDiv>
            <DMButton type="button" onClick={() => navigate(buildReelsPath('create'))} className="rf-reference-primary relative flex items-center gap-[0.55vw] overflow-hidden rounded-[12px] px-[1.35vw] py-[1.15vh] text-[1.45vh] font-semibold text-white">
              <Plus className="h-[1.8vh] w-[1.8vh]" strokeWidth={2.3} /> Generate Reel
              <DMText as="span" className="rf-button-shimmer absolute inset-0 pointer-events-none" />
            </DMButton>
          </DMDiv>

          <DMDiv className="rf-reference-card mt-[2vh] min-h-[68vh] flex-none p-[2.4vh]">
            {isLoadingReels ? (
              <DMDiv className="rf-reels-library-grid grid grid-cols-5 gap-[2.2vh]">
                {Array.from({ length: 5 }).map((_, index) => <DMDiv key={index} className="overflow-hidden rounded-[16px] border border-[#ECEAF6] bg-white"><DMDiv className="rf-shimmer h-[25vh]" /><DMDiv className="p-[0.8vw]"><DMDiv className="rf-shimmer h-[1.8vh] rounded" /><DMDiv className="rf-shimmer mt-[0.7vh] h-[1.2vh] w-2/3 rounded" /></DMDiv></DMDiv>)}
              </DMDiv>
            ) : visibleReels.length ? (
              <DMDiv className="rf-reels-library-grid grid grid-cols-5 gap-[2.2vh]">
                {visibleReels.map(item => {
                  const clientStatus = getReelClientStatuses()[item.id] || {};
                  const apiSharedPlatforms = [item.instagram && 'instagram', item.facebook && 'facebook', item.youtube && 'youtube', item.whatsapp && 'whatsapp', item.parentApp && 'parentApp', item.facultyApp && 'facultyApp'].filter(Boolean) as string[];
                  const sharedPlatforms = Array.from(new Set([...(clientStatus.sharedPlatforms || []), ...apiSharedPlatforms]));
                  const statusPlatforms = clientStatus.scheduledPlatforms?.length ? clientStatus.scheduledPlatforms : sharedPlatforms;
                  const isPublished = isReelPublished(item) || sharedPlatforms.length > 0;
                  return (
                  <DMDiv key={item.id} as="article" className="rf-reel-library-card group cursor-pointer overflow-hidden rounded-[16px] border border-[#ECEAF6] bg-white" onClick={() => navigate(buildReelsPath(`preview/${item.id}`), { state: { reelItem: item } })}>
                    <DMDiv className="relative h-[23.33vh] overflow-hidden bg-[#E7E3F5]">
                      {item.videoUrl ? <DMVideo src={`${item.videoUrl}#t=0.1`} muted preload="metadata" className="h-full w-full object-cover" /> : <DMImage src={item.thumbnail || sampleImg} alt={item.title} className="h-full w-full object-cover" />}
                      <DMDiv className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-black/35" />
                      <DMDiv className="absolute left-1/2 top-1/2 flex h-[5vh] w-[5vh] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#141028]/45 text-white backdrop-blur-[2px]"><Play className="ml-0.5 h-[2.1vh] w-[2.1vh] fill-current" /></DMDiv>
                      <DMText as="span" className="rf-reel-chip rf-reel-style-chip absolute left-[.89vh] top-[.89vh] text-white">{item.style || 'Cinematic'}</DMText>
                      <DMText as="span" className={`rf-reel-chip absolute right-[.89vh] top-[.89vh] ${sharedPlatforms.length ? 'bg-[#16A34A]/[.92] text-white' : clientStatus.scheduledDate ? 'bg-[#4F46E5]/[.92] text-white' : item.status === 'generating' ? 'bg-[#4F46E5]/[.92] text-white' : 'bg-white/90 text-[#4338CA]'}`}>{sharedPlatforms.length ? `Shared · ${sharedPlatforms.length}` : clientStatus.scheduledDate ? 'Scheduled' : item.status === 'generating' ? 'Generating' : 'Ready to share'}</DMText>
                      <DMDiv className="absolute bottom-[.89vh] left-[.89vh] flex items-center gap-[.56vh]"><DMDiv className="flex h-[1.67vh] w-[1.67vh] items-center justify-center rounded-[.44vh] bg-[#4F46E5] text-[0.94vh] font-extrabold text-white">S</DMDiv><DMText className="rf-reel-school-name font-bold text-white [text-shadow:0_1px_3px_rgba(0,0,0,.55)]">{item.school || 'Soomuch Demo School'}</DMText></DMDiv>
                    </DMDiv>
                    <DMDiv className="px-[1.55vh] py-[1.5vh]">
                      <DMDiv className="flex items-start justify-between gap-[0.5vw]"><DMDiv className="min-w-0"><DMText as="h2" className="truncate font-semibold">{item.title}</DMText><DMText className="mt-[0.33vh] text-[#9B9AB3]">{item.date} · {item.time}</DMText></DMDiv><DMDiv className="flex shrink-0 items-center gap-[.5vh]"><DMButton type="button" onClick={event => { event.stopPropagation(); if (item.videoUrl) window.open(item.videoUrl, '_blank', 'noopener,noreferrer'); }} className="flex h-[2.89vh] w-[2.89vh] shrink-0 items-center justify-center rounded-[.89vh] border border-[#ECEAF6] text-[#4F46E5] hover:border-[#D6D0F5] hover:bg-[#F5F3FB]" aria-label={`Download ${item.title}`}><Download className="h-[1.44vh] w-[1.44vh]" /></DMButton><DMDiv onClick={event => event.stopPropagation()} title={isPublished ? 'This reel is already published. Published reels cannot be deleted.' : 'Delete reel'}><DMButton type="button" disabled={isPublished} onClick={event => { event.stopPropagation(); handleDelete(item); }} className="flex h-[2.89vh] w-[2.89vh] shrink-0 items-center justify-center rounded-[.89vh] border border-[#F4C7C7] text-[#DC2626] hover:border-[#DC2626] hover:bg-[#FEF2F2] disabled:cursor-not-allowed disabled:border-[#ECEAF6] disabled:bg-[#F8F8FB] disabled:text-[#B7B5C4]" aria-label={isPublished ? `${item.title} is published and cannot be deleted` : `Delete ${item.title}`}><Trash2 className="h-[1.44vh] w-[1.44vh]" /></DMButton></DMDiv></DMDiv></DMDiv>
                      {!!statusPlatforms.length && <DMDiv className="mt-[.89vh] flex flex-wrap items-center gap-[.44vh]">{clientStatus.scheduledDate && <><CalendarDays className="h-[1.33vh] w-[1.33vh] text-[#4F46E5]" /><DMText className="rf-card-schedule-label font-semibold text-[#4338CA]">Scheduled for {formatScheduledDateTime(clientStatus.scheduledDate, clientStatus.scheduledTime)}</DMText></>}{statusPlatforms.map(key => { const platform = reelSharePlatforms.find(option => option.key === key); return platform ? <DMDiv key={key} title={platform.name} className="rf-platform-chip" style={{ background: platform.chip }}>{platform.mono}</DMDiv> : null; })}</DMDiv>}
                    </DMDiv>
                  </DMDiv>
                  );
                })}
              </DMDiv>
            ) : (
              <DMDiv className="flex h-full flex-col items-center justify-center text-center"><Film className="h-[5vh] w-[5vh] text-[#B6AFE8]" /><DMText className="mt-[1.3vh] text-[1.7vh] font-bold">No reels yet</DMText><DMText className="mt-[0.4vh] text-[1.3vh] text-[#9B9AB3]">Generate your first AI reel to see it here.</DMText><DMButton type="button" onClick={() => navigate(buildReelsPath('create'))} className="rf-reference-primary mt-[1.5vh] rounded-[12px] px-[1.4vw] py-[1vh] text-[1.35vh] font-semibold text-white">Generate Reel</DMButton></DMDiv>
            )}
          </DMDiv>
        </DMDiv>
        <DigitalMarketingDeleteModal isOpen={!!reelToDelete} title="Are you sure you want to delete this reel?" description={`This action cannot be undone. “${reelToDelete?.title || 'This reel'}” will be permanently removed.`} isDeleting={isDeletingReel} onCancel={handleCancelDelete} onConfirm={handleConfirmDelete} />
        <ReelPublishModal isOpen={!!reelToPublish} onClose={handleClosePublishModal} platforms={platforms} requestPublish={requestPublishPlatform} isPublishing={isPublishingReel} publishingPlatform={publishingPlatform} visiblePlatforms={reelToPublish?.availablePlatforms || []} />
      </>
    );
  }

  return (
    <DMDiv
      as="main"
      className="flex h-full min-h-0 flex-col overflow-hidden bg-white px-[2.5vw] py-[3.2vh] font-inter text-gray-950"
    >
      <DMDiv className="mb-[2.8vh] shrink-0">
        <DMDiv className="mb-[1.6vh] flex items-start justify-between gap-[2vw]">
          <DMDiv>
            <DigitalMarketingBreadcrumb
              className="mb-[1.2vh]"
              items={[{ label: 'Reels' }]}
            />
            <DMText
              as="h1"
              className="text-[3.5vh] font-bold leading-tight text-gray-950"
            >
              Reels
            </DMText>
            <DMText className="mt-[0.65vh] text-[1.65vh] font-medium leading-tight text-gray-600">
              Because every achievement deserves a wider audience.
            </DMText>
          </DMDiv>

          <DMDiv className="flex shrink-0 items-center gap-[0.8vw] pt-[4.8vh]">
            <DigitalMarketingSearchInput
              value={searchQuery}
              onChange={setSearchQuery}
            />
            {hasAnyReels && (
              <DMButton
                type="button"
                onClick={() => navigate(buildReelsPath('create'))}
                className="flex h-[6.4vh] items-center gap-[0.8vw] rounded-full bg-primary-main px-[2vw] text-[1.7vh] font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
              >
                <Plus className="h-[2.4vh] w-[2.4vh]" strokeWidth={2.1} />
                <DMText as="span">Generate Reel</DMText>
              </DMButton>
            )}
          </DMDiv>
        </DMDiv>
      </DMDiv>

      {!isLoadingReels && !hasAnyReels ? (
        <DMDiv className="flex min-h-0 flex-1 flex-col items-center justify-center text-center">
          <DMButton
            type="button"
            onClick={() => navigate(buildReelsPath('create'))}
            className="flex h-[6.6vh] items-center gap-[0.8vw] rounded-full bg-primary-main px-[2vw] text-[1.8vh] font-semibold text-white shadow-sm transition-opacity hover:opacity-90"
          >
            <Plus className="h-[2.5vh] w-[2.5vh]" strokeWidth={2.1} />
            <DMText as="span">Generate Reel</DMText>
          </DMButton>
          <DMText className="mt-[2vh] text-[1.75vh] font-medium text-gray-500">
            Not yet posted any thing
          </DMText>
        </DMDiv>
      ) : (
        <PaginatedCardGrid
          emptyMessage="No reels found."
          emptyClassName="h-[22vh]"
          filterSlot={
            <ManagementFilterBar
              className="mb-[2.2vh] shrink-0"
              options={reelFilterOptions}
              selectedValue={reelFilter}
              allValue="both"
              onChange={setReelFilter}
            />
          }
          hasItems={visibleReels.length > 0}
          isLoading={isLoadingReels}
          itemLabel="reel"
          pageIndex={pageIndex}
          pageSize={pageSize}
          pageSizeOptions={[10, 20, 30, 40, 50]}
          totalItems={totalReels}
          onPageIndexChange={setPageIndex}
          onPageSizeChange={setPageSize}
          renderSkeleton={index => <ReelCardSkeleton key={index} />}
        >
          {visibleReels.map(item => (
            <ReelCard
              key={item.id}
              item={item}
              activeMenuId={activeMenuId}
              setActiveMenuId={setActiveMenuId}
              onPlay={reel =>
                navigate(buildReelsPath(`preview/${reel.id}`), {
                  state: { reelItem: reel },
                })
              }
              onShare={handleShare}
              onDelete={handleDelete}
            />
          ))}
        </PaginatedCardGrid>
      )}

      <ReelPlayerModal
        item={selectedReel}
        onClose={() => setSelectedReel(null)}
      />
      <DigitalMarketingDeleteModal
        isOpen={!!reelToDelete}
        title="Are you sure you want to delete this reel?"
        description={`This action cannot be undone. “${reelToDelete?.title || 'This reel'}” will be permanently removed.`}
        isDeleting={isDeletingReel}
        onCancel={handleCancelDelete}
        onConfirm={handleConfirmDelete}
      />
      <ReelPublishModal
        isOpen={!!reelToPublish}
        onClose={handleClosePublishModal}
        platforms={platforms}
        requestPublish={requestPublishPlatform}
        isPublishing={isPublishingReel}
        publishingPlatform={publishingPlatform}
        visiblePlatforms={reelToPublish?.availablePlatforms || []}
      />
    </DMDiv>
  );
}

function ReelForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const authData = useAuth();
  const [reelForm, setReelForm] = React.useState<ReelFormValues>(
    () => location.state?.reelForm || emptyReelForm
  );
  const [uploadingPhotoSlots, setUploadingPhotoSlots] = React.useState(0);
  const [isGeneratingReel, setIsGeneratingReel] = React.useState(false);
  const [isGeneratingDescription, setIsGeneratingDescription] =
    React.useState(false);
  const [isGeneratingHashtags, setIsGeneratingHashtags] = React.useState(false);
  const [isLoadingStyles, setIsLoadingStyles] = React.useState(false);
  const [suggestedStyles, setSuggestedStyles] = React.useState<string[]>([]);
  const latestTitleRef = React.useRef(reelForm.title.trim());
  latestTitleRef.current = reelForm.title.trim();
  const [descriptionGenerationCount, setDescriptionGenerationCount] =
    React.useState(0);
  const [selectedCategory, setSelectedCategory] = React.useState(
    location.state?.reelForm?.category || ''
  );
  const [selectedSubcategory, setSelectedSubcategory] = React.useState(
    location.state?.reelForm?.subCategory || ''
  );
  const [selectedUploadedPhotoIndex, setSelectedUploadedPhotoIndex] =
    React.useState(0);
  const [previewImageUrl, setPreviewImageUrl] = React.useState('');
  const [wizardStep, setWizardStep] = React.useState<1 | 2 | 3>(1);
  const [isPreviewPlaying, setIsPreviewPlaying] = React.useState(false);
  const [isGenerationInfoOpen, setIsGenerationInfoOpen] = React.useState(false);
  const [isCategoryRequestOpen, setIsCategoryRequestOpen] = React.useState(false);
  const [categoryRequestMode, setCategoryRequestMode] = React.useState<'category' | 'subcategory'>('category');
  const [customCategory, setCustomCategory] = React.useState('');
  const [customSubcategory, setCustomSubcategory] = React.useState('');
  const { enhanceImage, isEnhancingImage } = useImageEnhancer();

  const handleUploadFile = async (file: File, showInlineError = true) => {
    const isAllowedMedia = ['image/png', 'image/jpg', 'image/jpeg'].includes(
      file.type
    );

    if (!isAllowedMedia) {
      if (showInlineError) {
        showGlobalModal('Only png, jpg, or jpeg files are allowed', 'error');
      }
      return false;
    }

    if (file.size > 15 * 1024 * 1024) {
      if (showInlineError) {
        showGlobalModal('Each image must be 15mb or less', 'error');
      }
      return false;
    }

    if ((reelForm.mediaUrls || []).length >= 10) {
      if (showInlineError) {
        showGlobalModal('You can upload a maximum of 10 photos', 'warning');
      }
      return false;
    }

    const localPreviewUrl = URL.createObjectURL(file);
    setUploadingPhotoSlots(current => current + 1);
    setReelForm(current => {
      const mediaUrls = [
        ...(current.mediaUrls || (current.mediaUrl ? [current.mediaUrl] : [])),
        localPreviewUrl,
      ].slice(0, 10);
      const mediaFileNames = [
        ...(current.mediaFileNames ||
          (current.mediaFileName ? [current.mediaFileName] : [])),
        file.name,
      ].slice(0, 10);

      return {
        ...current,
        mediaUrl: mediaUrls[0] || '',
        mediaFileName: mediaFileNames[0] || '',
        mediaUrls,
        mediaFileNames,
      };
    });

    try {
      const formData = new FormData();
      formData.append('dest', 'DM-CONTENT');
      formData.append('branch_id', authData?.BRANCH_ID as any);
      formData.append('database', authData?.DATABASE as any);
      formData.append('files', file);
      const response = await apiFileUpload(
        formData,
        'upload',
        '',
        '',
        true,
        false,
        true
      );
      const fileUrl = extractUploadedUrl(response);

      if (!fileUrl) {
        setReelForm(current => {
          const mediaUrls = (current.mediaUrls || []).filter(
            url => url !== localPreviewUrl
          );
          const mediaFileNames = (current.mediaFileNames || []).filter(
            (_, index) => (current.mediaUrls || [])[index] !== localPreviewUrl
          );

          return {
            ...current,
            mediaUrl: mediaUrls[0] || '',
            mediaFileName: mediaFileNames[0] || '',
            mediaUrls,
            mediaFileNames,
          };
        });
        if (showInlineError) {
          showGlobalModal('Unable to read uploaded file URL', 'error');
        }
        return false;
      }

      setReelForm(current => {
        const mediaUrls = (
          current.mediaUrls || (current.mediaUrl ? [current.mediaUrl] : [])
        ).map(url => (url === localPreviewUrl ? fileUrl : url));
        const mediaFileNames =
          current.mediaFileNames ||
          (current.mediaFileName ? [current.mediaFileName] : []);

        return {
          ...current,
          mediaUrl: mediaUrls[0] || '',
          mediaFileName: mediaFileNames[0] || '',
          mediaUrls,
          mediaFileNames,
        };
      });
      return true;
    } catch (error: any) {
      setReelForm(current => {
        const mediaUrls = (current.mediaUrls || []).filter(
          url => url !== localPreviewUrl
        );
        const mediaFileNames = (current.mediaFileNames || []).filter(
          (_, index) => (current.mediaUrls || [])[index] !== localPreviewUrl
        );

        return {
          ...current,
          mediaUrl: mediaUrls[0] || '',
          mediaFileName: mediaFileNames[0] || '',
          mediaUrls,
          mediaFileNames,
        };
      });
      if (showInlineError) {
        showGlobalModal(
          getApiErrorMessage(error, 'Failed to upload file'),
          'error'
        );
      }
      return false;
    } finally {
      setUploadingPhotoSlots(current => Math.max(current - 1, 0));
    }
  };

  const uploadedFileNames =
    reelForm.mediaFileNames ||
    (reelForm.mediaFileName ? [reelForm.mediaFileName] : []);
  const uploadedMediaUrls =
    reelForm.mediaUrls || (reelForm.mediaUrl ? [reelForm.mediaUrl] : []);
  const isUploadingMedia = uploadingPhotoSlots > 0;
  const hasUploadedPhotos = uploadedMediaUrls.length > 0;
  const hasUploadActivity = hasUploadedPhotos || isUploadingMedia;
  const availableUploadSlots = Math.max(
    10 - uploadedMediaUrls.length - uploadingPhotoSlots,
    0
  );
  const canUploadMorePhotos = availableUploadSlots > 0;
  const selectedUploadedPhotoUrl =
    uploadedMediaUrls[selectedUploadedPhotoIndex] || uploadedMediaUrls[0] || '';
  const descriptionGenerationsRemaining =
    maxDescriptionGenerations - descriptionGenerationCount;
  const descriptionGenerateButtonLabel =
    reelForm.description.trim() || descriptionGenerationCount > 0
      ? 'Re-generate'
      : 'Generate';
  const canGenerate = Boolean(
    reelForm.title.trim() &&
    reelForm.description.trim() &&
    selectedCategory &&
    selectedSubcategory &&
    reelForm.style &&
    uploadedMediaUrls.length &&
    !isUploadingMedia
  );

  React.useEffect(() => {
    if (
      selectedUploadedPhotoIndex > Math.max(uploadedMediaUrls.length - 1, 0)
    ) {
      setSelectedUploadedPhotoIndex(Math.max(uploadedMediaUrls.length - 1, 0));
    }
  }, [selectedUploadedPhotoIndex, uploadedMediaUrls.length]);

  React.useEffect(() => {
    if (!isPreviewPlaying || uploadedMediaUrls.length < 2) return undefined;
    const intervalId = window.setInterval(() => {
      setSelectedUploadedPhotoIndex(current =>
        (current + 1) % uploadedMediaUrls.length
      );
    }, 4000);
    return () => window.clearInterval(intervalId);
  }, [isPreviewPlaying, uploadedMediaUrls.length]);

  const handleRemoveUploadedPhoto = (photoIndex: number) => {
    setReelForm(current => {
      const mediaUrls = (
        current.mediaUrls || (current.mediaUrl ? [current.mediaUrl] : [])
      ).filter((_, index) => index !== photoIndex);
      const mediaFileNames = (
        current.mediaFileNames ||
        (current.mediaFileName ? [current.mediaFileName] : [])
      ).filter((_, index) => index !== photoIndex);

      return {
        ...current,
        mediaUrl: mediaUrls[0] || '',
        mediaFileName: mediaFileNames[0] || '',
        mediaUrls,
        mediaFileNames,
      };
    });

    setSelectedUploadedPhotoIndex(currentIndex => {
      if (photoIndex === currentIndex) {
        return Math.max(currentIndex - 1, 0);
      }

      if (photoIndex < currentIndex) {
        return Math.max(currentIndex - 1, 0);
      }

      return currentIndex;
    });
  };

  const handleEnhanceSelectedPhoto = async () => {
    const sourceUrl = selectedUploadedPhotoUrl;
    const photoIndex = selectedUploadedPhotoIndex;
    const enhancedUrl = await enhanceImage(sourceUrl);
    if (!enhancedUrl) return;

    setReelForm(current => {
      const mediaUrls = [
        ...(current.mediaUrls || (current.mediaUrl ? [current.mediaUrl] : [])),
      ];
      if (mediaUrls[photoIndex] !== sourceUrl) return current;
      mediaUrls[photoIndex] = enhancedUrl;
      return { ...current, mediaUrl: mediaUrls[0] || '', mediaUrls };
    });
  };

  const getFilesFromFileList = (files: FileList | null) => {
    const selectedFiles: File[] = [];

    if (!files) {
      return selectedFiles;
    }

    for (let index = 0; index < files.length; index += 1) {
      const file = files.item(index);

      if (file) {
        selectedFiles.push(file);
      }
    }

    return selectedFiles;
  };

  const renderPhotoInput = (label: string, className: string) => (
    <DMLabel className={className}>
      <UploadCloud className="h-[2.1vh] w-[2.1vh]" strokeWidth={2} />
      <DMText as="span">{isUploadingMedia ? 'Uploading...' : label}</DMText>
      <DMInput
        type="file"
        accept="image/png,image/jpg,image/jpeg"
        className="hidden"
        disabled={isUploadingMedia || !canUploadMorePhotos}
        multiple
        onChange={async event => {
          const input = event.currentTarget;
          const files = getFilesFromFileList(input.files);

          await handleUploadFiles(files);
          input.value = '';
        }}
      />
    </DMLabel>
  );

  const showUploadFailedModal = () => {
    showGlobalModal(
      <DMText as="span" className="block">
        <DMText
          as="span"
          className="block text-[2.6vh] font-bold text-gray-950"
        >
          Some images failed to upload
        </DMText>
        <DMText
          as="span"
          className="mt-[1.2vh] block text-[1.85vh] font-medium text-gray-500"
        >
          Some images could not be uploaded. Please try again
        </DMText>
      </DMText>,
      'error',
      { showConfirm: true, onConfirm: () => undefined, confimLabel: 'ok' }
    );
  };

  const handleUploadFiles = async (files: File[]) => {
    const filesToUpload = files.slice(0, availableUploadSlots);

    if (!filesToUpload.length) {
      if (!canUploadMorePhotos) {
        showGlobalModal('You can upload a maximum of 10 photos', 'warning');
      }
      return;
    }

    const uploadResults = await Promise.all(
      filesToUpload.map(file => handleUploadFile(file, false))
    );
    const failedUploads = uploadResults.filter(result => !result).length;

    if (failedUploads) {
      showUploadFailedModal();
    }
  };

  const showGenerationStartedModal = () => {
    setIsGenerationInfoOpen(true);
  };

  const showGenerationFailedModal = () => {
    showGlobalModal(
      <DMText as="span" className="block">
        <DMText
          as="span"
          className="block text-[2.6vh] font-bold text-gray-950"
        >
          Video generation failed
        </DMText>
        <DMText
          as="span"
          className="mt-[1.2vh] block text-[1.85vh] font-medium text-gray-500"
        >
          Video generation failed. Please try again.
        </DMText>
      </DMText>,
      'warning',
      {
        showConfirm: true,
        onConfirm: handleGenerateReelFromForm,
        confimLabel: 'Retry',
      }
    );
  };

  const handleGenerateDescription = async () => {
    const requestTitle = reelForm.title.trim();
    if (descriptionGenerationsRemaining <= 0) {
      showGlobalModal('Description generation limit reached.', 'warning');
      return;
    }

    if (!reelForm.title.trim() || !selectedCategory.trim()) {
      showGlobalModal(
        'Complete title and category before generating a description.',
        'warning'
      );
      return;
    }

    setIsGeneratingDescription(true);

    try {
      const description = await reelsApi.generateDescription({
        content_type: 'REEL',
        category: selectedCategory,
        sub_category: selectedSubcategory,
        title: requestTitle,
      });

      if (description) {
        if (latestTitleRef.current !== requestTitle) return;
        setReelForm(current => ({
          ...current,
          description,
        }));
        setDescriptionGenerationCount(current =>
          Math.min(current + 1, maxDescriptionGenerations)
        );
      } else {
        showGlobalModal('Unable to generate description', 'error');
      }
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to generate description'),
        'error'
      );
    } finally {
      setIsGeneratingDescription(false);
    }
  };

  const handleGenerateHashtags = async () => {
    const requestTitle = reelForm.title.trim();
    if (!requestTitle || !selectedCategory || !selectedSubcategory) {
      showGlobalModal(
        'Complete title, category, and subcategory before generating hashtags.',
        'warning'
      );
      return;
    }

    setIsGeneratingHashtags(true);
    try {
      const hashtags = await reelsApi.generateHashtags({
        content_type: 'REEL',
        category: selectedCategory,
        sub_category: selectedSubcategory,
        title: requestTitle,
      });
      if (latestTitleRef.current !== requestTitle) return;
      if (!hashtags.length) {
        showGlobalModal('Unable to generate hashtags', 'error');
        return;
      }
      setReelForm(current => ({ ...current, hashtags: hashtags.join(' ') }));
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to generate hashtags'),
        'error'
      );
    } finally {
      setIsGeneratingHashtags(false);
    }
  };

  async function handleGenerateReelFromForm() {
    if (!canGenerate) {
      showGlobalModal(
        'Complete required reel details before generating',
        'warning'
      );
      return;
    }

    setIsGeneratingReel(true);
    showGenerationStartedModal();

    try {
      const result = await reelsApi.generate({
        source: 'REEL',
        title: reelForm.title,
        description: reelForm.description,
        category: selectedCategory,
        sub_category: selectedSubcategory,
        style: reelForm.style || 'cinematic',
        images: uploadedMediaUrls,
      });
      if (result?.video_id) {
        savePendingReelGeneration({
          reelId: Number(result.reel_id || 0),
          videoId: result.video_id,
          title: reelForm.title,
        });
      }
    } catch (error) {
      setIsGenerationInfoOpen(false);
      showGenerationFailedModal();
    } finally {
      setIsGeneratingReel(false);
    }
  }

  const renderUploadSkeleton = (_index: number) => null;

  const wizardSteps = [
    { number: 1, title: 'Details', subtitle: 'Category, title & copy', icon: Wand2 },
    { number: 2, title: 'Photos', subtitle: 'Upload & generate', icon: ImagePlus },
  ] as const;
  const selectedCategoryDesign = reelDesignCategories.find(category => category.label === selectedCategory);
  const previewBgmLabel = ({ campus: 'Ambient Corporate', testimonials: 'Warm Acoustic', admissions: 'Uplifting Corporate', events: 'Festive Pop', achievements: 'Triumphant Orchestral', academics: 'Bright Inspirational', educational: 'Calm Piano', extracurricular: 'Energetic Pop', community: 'Heartfelt Strings', sports: 'High-Energy Beat', graduation: 'Heartfelt Strings' } as Record<string, string>)[selectedCategoryDesign?.key || ''] || 'Uplifting Corporate';
  const previewTransitionLabel = ({ Cinematic: 'Slow Crossfade', 'Beat Sync': 'Cut on the Beat', 'Slow Motion': 'Slow Motion', 'Dynamic Montage': 'Dynamic Cuts', Storytelling: 'Crossfade + Ken Burns', 'Minimal Clean': 'Clean Crossfade' } as Record<string, string>)[reelForm.style] || 'Crossfade + Ken Burns';
  const detailsComplete = Boolean(selectedCategory && selectedSubcategory && reelForm.title.trim() && reelForm.description.trim() && reelForm.hashtags?.trim());
  const stepComplete = wizardStep === 1 ? detailsComplete : hasUploadedPhotos && !isUploadingMedia;
  const wizardHint = !selectedCategory
    ? 'Pick a category to get started.'
    : !selectedSubcategory
      ? 'Choose a subcategory to continue.'
      : wizardStep === 2 && !hasUploadedPhotos
          ? 'Add at least one photo to generate your reel.'
          : wizardStep === 2
            ? 'Looking great. This reel is ready to generate.'
            : '';
  const goToNextStep = async () => {
    if (!stepComplete || wizardStep !== 1) return;
    setIsLoadingStyles(true);
    try {
      const styles = await reelsApi.getStyles({
        title: reelForm.title.trim(),
        description: reelForm.description.trim(),
        category: selectedCategory,
        sub_category: selectedSubcategory,
      });
      if (!styles.length) {
        showGlobalModal(
          'No presentation styles were returned. Please try again.',
          'warning'
        );
        return;
      }
      setSuggestedStyles(styles);
      setReelForm(current => ({ ...current, style: styles[0] }));
      setWizardStep(2);
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to load presentation styles'),
        'error'
      );
    } finally {
      setIsLoadingStyles(false);
    }
  };
  const resetReelWizard = () => {
    setWizardStep(1);
    setSelectedCategory('');
    setSelectedSubcategory('');
    setSuggestedStyles([]);
    setReelForm({ ...emptyReelForm });
  };
  const useWizardDesign = wizardStep >= 1;

  if (useWizardDesign) {
    return (
      <DMDiv as="main" className="reel-flow rf-enter relative flex h-full w-full min-h-0 max-w-none flex-col self-stretch overflow-hidden px-[1.7vw] py-[1.56vh] pb-[2vh] font-inter text-[#161328]">
        <DMDiv className="flex shrink-0 items-center gap-[0.8vw]">
          <DMButton type="button" onClick={() => navigate('/digital-marketing/dashboard')} className="rf-create-header-icon flex h-[4.22vh] w-[4.22vh] items-center justify-center rounded-[11px] border border-[#D6D3E6] bg-white text-[#4F46E5] transition-transform hover:-translate-x-0.5" aria-label="Back to dashboard">
            <ChevronLeft className="h-[2.2vh] w-[2.2vh]" />
          </DMButton>
          <DMDiv>
            <DMText as="h1" className="text-[3vh] font-semibold leading-none tracking-[-0.02em]">Generate Reel</DMText>
            <DMText className="mt-[0.5vh] text-[1.45vh] text-[#9B9AB3]">Because every achievement deserves a wider audience.</DMText>
          </DMDiv>
          <DMDiv className="flex-1" />
          <DMButton type="button" onClick={() => navigate(REELS_BASE_PATH, { state: { refreshReels: Date.now() } })} className="flex h-[4.6vh] items-center gap-[0.5vw] rounded-[11px] border border-[#D6D3E6] bg-white px-[1.1vw] text-[1.4vh] font-semibold hover:border-[#C9C2F5] hover:text-[#4338CA]">
            <Clock3 className="h-[1.8vh] w-[1.8vh]" /> View Past Reels
          </DMButton>
        </DMDiv>

        <DMDiv className="mt-[1.5vh] flex shrink-0 gap-[0.4vw]" aria-label={`Step ${wizardStep} of 2`}>
          {wizardSteps.map(step => <DMDiv key={step.number} className={`h-[0.55vh] flex-1 rounded-full transition-colors ${step.number <= wizardStep ? 'bg-[#4F46E5]' : 'bg-[#DDD9EA]'}`} />)}
        </DMDiv>

        <DMDiv className="rf-wizard-layout mt-[1.56vh] grid min-h-0 w-full max-w-none flex-1 grid-cols-[16vw_minmax(0,1fr)] gap-[1.1vw] overflow-hidden">
          <DMDiv as="nav" className="rf-card rf-step-rail h-fit p-[1.78vh_0.7vw]" aria-label="Generate reel steps">
            {wizardSteps.map((step, index) => {
              const Icon = step.icon;
              const done = wizardStep > step.number || (wizardStep === 2 && step.number === 2 && canGenerate);
              const active = wizardStep === step.number;
              const available = step.number === 1 || (step.number === 2 && detailsComplete);
              return (
                <DMButton key={step.number} type="button" disabled={!available} onClick={() => setWizardStep(step.number)} className={`rf-step-item flex w-full gap-[0.7vw] rounded-[12px] px-[0.7vw] py-[1.1vh] text-left ${active ? 'bg-[#F3F1FC]' : 'hover:bg-[#FAFAFC]'} disabled:cursor-not-allowed`}>
                  <DMDiv className="flex flex-col items-center">
                    <DMDiv className={`flex h-[2.89vh] w-[2.89vh] items-center justify-center rounded-full border-2 text-[1.25vh] font-bold ${done ? 'border-[#16A34A] bg-[#16A34A] text-white' : active ? 'border-[#4F46E5] bg-[#4F46E5] text-white' : 'border-[#D6D3E6] bg-white text-[#9B9AB3]'}`}>
                      {done ? <Check className="h-[1.6vh] w-[1.6vh]" /> : <Icon className="h-[1.5vh] w-[1.5vh]" />}
                    </DMDiv>
                    {index < wizardSteps.length - 1 && <DMDiv className={`mt-[0.33vh] h-[2.22vh] w-[0.22vh] ${done ? 'bg-[#16A34A]' : 'bg-[#ECEAF6]'}`} />}
                  </DMDiv>
                  <DMDiv className="pt-[0.2vh]">
                    <DMText className={`text-[1.45vh] font-bold ${active ? 'text-[#4338CA]' : 'text-[#161328]'}`}>{step.title}</DMText>
                    <DMText className="mt-[0.2vh] text-[1.2vh] text-[#9B9AB3]">{step.subtitle}</DMText>
                  </DMDiv>
                </DMButton>
              );
            })}
          </DMDiv>

          <DMDiv className="rf-card flex h-full w-full min-h-0 min-w-0 max-w-none flex-col overflow-hidden p-[1.78vh_1.5vw_1.56vh]">
            <DMDiv className="rf-content-grid grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)_22vw] gap-[1.4vw] overflow-hidden">
              <DMDiv className="rf-form-scroll min-h-0 overflow-y-auto px-[0.9vw]">
                {wizardStep === 1 && (
                  <DMDiv className="rf-enter">
                    <DMText as="h2" className="text-[2.1vh] font-bold">What's this reel about?</DMText>
                    <DMText className="mt-[0.4vh] text-[1.35vh] text-[#9B9AB3]">Category, title, description & hashtags — all in one place.</DMText>
                    <DMText className="mt-[1.7vh] text-[1.35vh] font-bold">Category</DMText>
                    {!selectedCategory ? <DMDiv className="rf-category-grid mt-[1vh] grid gap-[1.1vh]">
                      {reelDesignCategories.map(category => {
                        const CategoryIcon = category.icon;
                        return <DMButton key={category.key} type="button" onClick={() => { setSelectedCategory(category.label); setSelectedSubcategory(''); setReelForm(current => ({ ...current, category: category.label, subCategory: '', style: '', title: '', description: '', hashtags: '' })); }} className="flex min-h-[5.4vh] items-center gap-[0.6vw] rounded-[13px] border-[1.5px] border-[#DDD9E8] bg-white px-[0.8vw] py-[1vh] text-left text-[1.25vh] font-bold transition-transform hover:-translate-y-0.5 hover:border-[#7C6FF0]">
                          <DMText as="span" className="flex h-[3.1vh] w-[3.1vh] items-center justify-center rounded-[9px] bg-[#F3F1FC] text-[#4F46E5]"><CategoryIcon className="h-[1.7vh] w-[1.7vh]" /></DMText>{category.label}
                        </DMButton>;
                      })}
                      <DMButton type="button" onClick={() => { setCategoryRequestMode('category'); setCustomCategory(''); setCustomSubcategory(''); setIsCategoryRequestOpen(true); }} className="col-span-full flex items-center gap-[0.7vw] rounded-[14px] border-[1.5px] border-[#F5DBAE] bg-[#FFF6E9] px-[0.9vw] py-[1vh] text-left hover:border-[#E0A020]"><DMDiv className="flex h-[3.8vh] w-[3.8vh] items-center justify-center rounded-[11px] bg-[#FFF1E0] text-[#E07A3C]"><Plus className="h-[1.8vh] w-[1.8vh]" /></DMDiv><DMDiv className="flex-1"><DMText className="text-[1.3vh] font-bold">Can't find your category?</DMText><DMText className="mt-[0.15vh] text-[1.1vh] text-[#8A7A5C]">Enter your category name</DMText></DMDiv><ArrowRight className="h-[1.7vh] w-[1.7vh] text-[#E07A3C]" /></DMButton>
                    </DMDiv> : <DMDiv className="mt-[0.8vh] flex items-center gap-[0.65vw] rounded-[13px] border-[1.5px] border-[#4F46E5] bg-[#EEEAFB] px-[0.8vw] py-[0.8vh]"><DMDiv className="flex h-[3.1vh] w-[3.1vh] items-center justify-center rounded-[9px] bg-[#4F46E5] text-white">{selectedCategoryDesign ? React.createElement(selectedCategoryDesign.icon, { className: 'h-[1.7vh] w-[1.7vh]' }) : <Sparkles className="h-[1.7vh] w-[1.7vh]" />}</DMDiv><DMText className="flex-1 text-[1.3vh] font-bold text-[#4338CA]">{selectedCategory}</DMText><DMButton type="button" onClick={() => { setSelectedCategory(''); setSelectedSubcategory(''); setReelForm(current => ({ ...current, category: '', subCategory: '' })); }} className="rounded-[8px] px-[0.6vw] py-[0.5vh] text-[1.15vh] font-semibold text-[#4338CA] hover:bg-[#E2DDFB]">Change</DMButton></DMDiv>}

                    {selectedCategoryDesign && <DMDiv className="mt-[1.2vh]"><DMText className="text-[1.3vh] font-bold">Subcategory — {selectedCategory}</DMText>{!selectedSubcategory ? <DMDiv className="mt-[0.8vh] flex flex-wrap gap-[0.55vw]">{selectedCategoryDesign.subs.map(sub => <DMButton key={sub} type="button" onClick={() => { setSelectedSubcategory(sub); setReelForm(current => ({ ...current, subCategory: sub, style: '', title: '', description: '', hashtags: '' })); }} className="rounded-[11px] border-[1.5px] border-[#D6D3E6] bg-white px-[0.9vw] py-[0.8vh] text-[1.25vh] font-semibold hover:border-[#4F46E5] hover:text-[#4338CA]">{sub}</DMButton>)}</DMDiv> : <DMDiv className="mt-[0.8vh] flex items-center rounded-[13px] border-[1.5px] border-[#4F46E5] bg-[#EEEAFB] px-[0.8vw] py-[0.8vh]"><DMText className="flex-1 text-[1.3vh] font-bold text-[#4338CA]">{selectedSubcategory}</DMText><DMButton type="button" onClick={() => { setSelectedSubcategory(''); setReelForm(current => ({ ...current, subCategory: '', style: '' })); }} className="rounded-[8px] px-[0.6vw] py-[0.5vh] text-[1.15vh] font-semibold text-[#4338CA] hover:bg-[#E2DDFB]">Change</DMButton></DMDiv>}</DMDiv>}

                    {selectedCategoryDesign && !selectedSubcategory && <DMButton type="button" onClick={() => { setCategoryRequestMode('subcategory'); setCustomCategory(selectedCategory); setCustomSubcategory(''); setIsCategoryRequestOpen(true); }} className="mt-[0.9vh] flex w-full items-center gap-[0.7vw] rounded-[14px] border-[1.5px] border-[#F5DBAE] bg-[#FFF6E9] px-[0.9vw] py-[1vh] text-left hover:border-[#E0A020]"><DMDiv className="flex h-[3.8vh] w-[3.8vh] items-center justify-center rounded-[11px] bg-[#FFF1E0] text-[#E07A3C]"><Plus className="h-[1.8vh] w-[1.8vh]" /></DMDiv><DMDiv className="flex-1"><DMText className="text-[1.3vh] font-bold">Can't find your subcategory?</DMText><DMText className="mt-[0.15vh] text-[1.1vh] text-[#8A7A5C]">Enter your subcategory name</DMText></DMDiv><ArrowRight className="h-[1.7vh] w-[1.7vh] text-[#E07A3C]" /></DMButton>}

                    {selectedSubcategory && <DMDiv className="mt-[1.2vh]"><DMLabel className="text-[1.3vh] font-bold">Reel Title</DMLabel><DMText className="mt-[0.2vh] text-[1.15vh] text-[#9B9AB3]">Give this {selectedSubcategory} reel a specific name</DMText><DMInput className="rf-control mt-[0.65vh] text-[1.35vh]" maxLength={80} placeholder={`${selectedSubcategory} Highlights`} value={reelForm.title} onChange={event => setReelForm(current => ({ ...current, title: event.target.value }))} /></DMDiv>}

                    {selectedSubcategory && <DMDiv className="rf-progressive-section mt-[1.6vh] border-t border-[#F0EEF7] pt-[1.45vh]">
                      <DMText className="text-[1.5vh] font-bold">AI description &amp; hashtags</DMText>
                      <DMText className="mt-[0.35vh] text-[1.24vh] text-[#9B9AB3]">Add your own copy, or use Generate when you want an AI suggestion.</DMText>

                      <DMDiv className="mt-[1.35vh] flex items-center justify-between">
                        <DMLabel className="text-[1.35vh] font-bold">Description</DMLabel>
                        <DMButton type="button" onClick={handleGenerateDescription} disabled={!reelForm.title.trim() || isGeneratingDescription || descriptionGenerationsRemaining <= 0} className="rf-text-action"><RefreshCw className={`h-[1.45vh] w-[1.45vh] ${isGeneratingDescription ? 'rf-spin' : ''}`} /> {isGeneratingDescription ? 'Generating…' : 'Generate'}</DMButton>
                      </DMDiv>
                      {isGeneratingDescription ? <DMDiv className="rf-ai-generation-card mt-[0.65vh]">
                        <DMDiv className="rf-ai-sparkle"><Sparkles className="h-[1.75vh] w-[1.75vh]" /></DMDiv><DMDiv className="min-w-0 flex-1"><DMText className="text-[1.25vh] font-semibold text-[#4338CA]">Drafting your description…</DMText><DMDiv className="mt-[0.75vh] h-[0.65vh] overflow-hidden rounded-full bg-[#ECEAF6]"><DMDiv className="rf-ai-progress h-full w-[90%] rounded-full bg-gradient-to-r from-[#7C6FF0] to-[#4F46E5]" /></DMDiv></DMDiv><DMText className="text-[1.2vh] font-bold text-[#4338CA]">90%</DMText>
                      </DMDiv> : <DMTextarea className="rf-control mt-[0.65vh] h-[7.8vh] resize-none" placeholder="Enter a description" value={reelForm.description} onChange={event => setReelForm(current => ({ ...current, description: event.target.value }))} />}
                      <DMText className="mt-[0.45vh] text-right text-[1.08vh] text-[#9B9AB3]">{descriptionGenerationsRemaining} retries remaining</DMText>

                      <DMDiv className="mt-[1.15vh] flex items-center justify-between"><DMLabel className="text-[1.35vh] font-bold">Hashtags</DMLabel><DMButton type="button" onClick={handleGenerateHashtags} disabled={!reelForm.title.trim() || isGeneratingHashtags} className="rf-text-action"><RefreshCw className={`h-[1.45vh] w-[1.45vh] ${isGeneratingHashtags ? 'rf-spin' : ''}`} /> {isGeneratingHashtags ? 'Generating…' : 'Generate'}</DMButton></DMDiv>
                      {isGeneratingHashtags ? <DMDiv className="rf-ai-generation-card mt-[0.65vh]"><DMDiv className="rf-ai-sparkle"><Sparkles className="h-[1.75vh] w-[1.75vh]" /></DMDiv><DMDiv className="min-w-0 flex-1"><DMText className="text-[1.25vh] font-semibold text-[#4338CA]">Selecting trending hashtags…</DMText><DMDiv className="mt-[0.75vh] h-[0.65vh] overflow-hidden rounded-full bg-[#ECEAF6]"><DMDiv className="rf-ai-progress h-full w-[85%] rounded-full bg-gradient-to-r from-[#7C6FF0] to-[#4F46E5]" /></DMDiv></DMDiv><DMText className="text-[1.2vh] font-bold text-[#4338CA]">85%</DMText></DMDiv> : <DMTextarea className="rf-control mt-[0.65vh] h-[5.6vh] resize-none" placeholder="#SchoolLife #Achievements #Soomuch" value={reelForm.hashtags || ''} onChange={event => setReelForm(current => ({ ...current, hashtags: event.target.value }))} />}
                    </DMDiv>}
                  </DMDiv>
                )}

                {wizardStep === 2 && (
                  <DMDiv className="rf-enter flex h-full min-h-[46vh] flex-col">
                    <DMDiv className="flex items-center gap-[0.75vw]"><DMButton type="button" onClick={() => setWizardStep(1)} className="flex h-[3.1vh] w-[3.1vh] items-center justify-center rounded-[0.9vh] border border-[#D6D3E6] bg-white text-[#4F46E5]" aria-label="Back to details"><ChevronLeft className="h-[1.7vh] w-[1.7vh]" /></DMButton><DMText as="h2" className="text-[2.1vh] font-bold">Add your media</DMText></DMDiv>
                    <DMText className="mt-[0.55vh] text-[1.35vh] text-[#9B9AB3]">Upload up to 10 photos — the preview updates automatically.</DMText>
                    <DMDiv className="mt-[2vh] flex items-center justify-between"><DMLabel className="text-[1.35vh] font-bold">Upload up to 10 photos</DMLabel>{hasUploadedPhotos && <DMText as="span" className="rounded-full bg-[#E9F9F0] px-[1vh] py-[0.34vh] text-[1.2vh] font-bold text-[#16A34A]">{uploadedMediaUrls.length}/10 Photos Uploaded</DMText>}</DMDiv>
                    {!hasUploadActivity ? <DMDiv onDragOver={event => event.preventDefault()} onDrop={async event => { event.preventDefault(); await handleUploadFiles(getFilesFromFileList(event.dataTransfer.files)); }} className="rf-media-dropzone mt-[0.75vh] flex min-h-[23.33vh] items-center justify-center rounded-[1.55vh] border-[0.22vh] border-dashed border-[#D6D0F5] bg-[#FAFAFC] text-center">
                      <DMDiv><DMDiv className="relative mx-auto h-[5.77vh] w-[7.11vh]"><DMDiv className="rf-floating-photo absolute left-[0.22vh] top-[0.88vh] h-[4.22vh] w-[3.33vh] rounded-[0.88vh] border-[0.22vh] border-white bg-[#EEEAFB] shadow-[0_0.66vh_1.55vh_rgba(16,24,40,.1)]" /><DMDiv className="rf-floating-photo rf-floating-photo-delay absolute left-[2.66vh] top-[0.22vh] flex h-[4.88vh] w-[3.77vh] items-center justify-center rounded-[1vh] border-[0.22vh] border-white bg-gradient-to-br from-[#C9C2F5] to-[#7C6FEE] text-white shadow-[0_0.88vh_2vh_rgba(79,63,224,.24)]"><Plus className="h-[1.77vh] w-[1.77vh]" /></DMDiv></DMDiv><DMText className="mt-[0.66vh] text-[1.55vh] font-semibold">Choose a file or drag &amp; drop it here</DMText><DMText className="mt-[0.33vh] text-[1.27vh] text-[#9B9AB3]">png, jpg, jpeg (Max 15mb)</DMText>{renderPhotoInput('Upload Photos', `mx-auto mt-[1.22vh] flex h-[4.33vh] items-center justify-center rounded-[1.22vh] border-[0.17vh] border-[#C9C2F5] bg-white px-[1.38vw] text-[1.5vh] font-semibold text-[#4338CA] ${!canUploadMorePhotos ? 'pointer-events-none opacity-50' : ''}`)}</DMDiv>
                    </DMDiv> : <DMDiv className="mt-[1vh] rounded-[1.66vh] border border-[#ECEAF6] bg-[#FAFAFC] p-[1.33vh]"><DMDiv className="grid grid-cols-4 gap-[1.1vh]">{uploadedMediaUrls.map((url,index) => <DMDiv key={`${url}-${index}`} className="rf-uploaded-photo group relative h-[10vh] overflow-hidden rounded-[1.33vh] border-[0.22vh] border-white bg-white shadow-[0_0.66vh_1.77vh_rgba(16,24,40,.1)]"><DMButton type="button" onClick={() => setPreviewImageUrl(url)} className="h-full w-full border-0 p-0"><DMImage src={url} alt={`Reel photo ${index+1}`} className="h-full w-full object-cover" /></DMButton><DMButton type="button" aria-label={`Remove photo ${index+1}`} onClick={() => handleRemoveUploadedPhoto(index)} className="rf-photo-remove absolute right-[0.55vh] top-[0.55vh] flex h-[2.22vh] min-h-0 w-[2.22vh] items-center justify-center rounded-full bg-[rgba(20,16,40,.55)] p-0 text-white"><X className="h-[1.22vh] w-[1.22vh]" /></DMButton></DMDiv>)}{Array.from({length:uploadingPhotoSlots}).map((_,index) => renderUploadSkeleton(index))}{canUploadMorePhotos && renderPhotoInput('Add more', 'rf-add-photo flex h-[10vh] flex-col items-center justify-center gap-[0.45vh] rounded-[1.33vh] border-[0.17vh] border-dashed border-[#D6D0F5] bg-white text-[1.1vh] font-semibold text-[#7C6FF0]')}</DMDiv></DMDiv>}
                    {isUploadingMedia && <DMDiv className="mt-[1vh] flex items-center gap-[1.3vh] rounded-[1.44vh] border border-[#D6D0F5] bg-[#F5F3FB] px-[1.55vh] py-[1.22vh]"><DMDiv className="flex h-[3.33vh] w-[3.33vh] items-center justify-center rounded-[1vh] bg-gradient-to-br from-[#6D5AE6] to-[#4F46E5] text-white"><UploadCloud className="h-[1.66vh] w-[1.66vh]" /></DMDiv><DMDiv className="min-w-0 flex-1"><DMText className="text-[1.38vh] font-semibold text-[#4338CA]">Uploading photos…</DMText><DMDiv className="mt-[0.66vh] h-[0.66vh] overflow-hidden rounded-full bg-[#E6E2FB]"><DMDiv className="rf-uploading-bar h-full w-full rounded-full bg-gradient-to-r from-[#6D5AE6] to-[#4338CA]" /></DMDiv></DMDiv></DMDiv>}
                    <DMText className="mt-[1.1vh] flex items-center gap-[0.65vh] text-[1.2vh] text-[#5B4FE0]"><Sparkles className="h-[1.55vh] w-[1.55vh]" /> Low quality images? Our AI Image Enhancer will take care of it!</DMText>
                    <DMDiv className="mt-[2vh] border-t border-[#F0EEF7] pt-[1.77vh]"><DMDiv className="flex items-center gap-[0.75vh]"><Sparkles className="h-[1.66vh] w-[1.66vh] text-[#5B4FE0]" /><DMLabel className="text-[1.35vh] font-bold">AI-suggested presentation style</DMLabel></DMDiv><DMDiv className="mt-[1vh] flex flex-wrap gap-[0.88vh]">{suggestedStyles.map(style => { const StyleIcon = style.includes('Slow') ? Clock3 : style.includes('Story') ? BookOpen : style.includes('Beat') || style.includes('Dynamic') ? Music2 : style.includes('Highlight') || style.includes('Achievement') ? Trophy : style.includes('Celebration') ? PartyPopper : Film; const active = reelForm.style === style; return <DMButton key={style} type="button" onClick={() => setReelForm(current => ({...current, style}))} className={`rf-style-option flex items-center gap-[0.66vh] rounded-full border-[0.17vh] py-[0.77vh] pl-[1vh] pr-[1.44vh] font-semibold ${active ? 'border-[#4F46E5] bg-[#EEEAFB] text-[#4338CA]' : 'border-[#D6D3E6] bg-white text-[#1B1830]'}`}><DMText as="span" className={`flex h-[2.22vh] w-[2.22vh] items-center justify-center rounded-full ${active ? 'bg-[#4F46E5] text-white' : 'bg-[#EEEAFB] text-[#4F46E5]'}`}><StyleIcon className="h-[1.3vh] w-[1.3vh]" /></DMText>{style}</DMButton>;})}</DMDiv></DMDiv>
                  </DMDiv>
                )}

                {wizardStep === 3 && <DMDiv className="rf-enter flex h-full flex-col justify-center"><DMDiv className="mx-auto max-w-[35vw] text-center"><DMDiv className="mx-auto flex h-[7vh] w-[7vh] items-center justify-center rounded-[18px] bg-gradient-to-br from-[#6D5AE6] to-[#4F46E5] text-white shadow-[0_15px_34px_rgba(79,63,224,.32)]"><Film className="h-[3.2vh] w-[3.2vh]" /></DMDiv><DMText as="h2" className="mt-[1.7vh] text-[2.3vh] font-bold">Ready to create your reel</DMText><DMText className="mt-[0.7vh] text-[1.35vh] leading-relaxed text-[#77758F]">AI will combine {uploadedMediaUrls.length} photo{uploadedMediaUrls.length === 1 ? '' : 's'} with {reelForm.style || 'cinematic'} transitions and an uplifting soundtrack. Generation can take up to 20 minutes.</DMText><DMDiv className="mt-[1.7vh] grid grid-cols-3 gap-[0.7vw] text-left">{[{label:'Photos',value:String(uploadedMediaUrls.length),icon:ImagePlus},{label:'Style',value:reelForm.style || 'Cinematic',icon:Wand2},{label:'Duration',value:'~30 sec',icon:Clock3}].map(item => <DMDiv key={item.label} className="rounded-[12px] border border-[#ECEAF6] bg-[#FAFAFC] p-[0.8vw]"><item.icon className="h-[1.8vh] w-[1.8vh] text-[#4F46E5]" /><DMText className="mt-[0.6vh] text-[1.05vh] uppercase tracking-wider text-[#9B9AB3]">{item.label}</DMText><DMText className="mt-[0.2vh] truncate text-[1.3vh] font-bold capitalize">{item.value}</DMText></DMDiv>)}</DMDiv></DMDiv></DMDiv>}
              </DMDiv>

              <DMDiv className={`rf-preview rf-create-preview flex min-h-0 flex-col ${wizardStep === 2 ? 'is-media-step' : 'is-details-step'}`}>
                <DMDiv className="flex items-center gap-[0.45vw]"><DMDiv className={`rf-preview-status h-[0.66vh] w-[0.66vh] rounded-full ${isUploadingMedia ? 'bg-[#7C6FEE]' : 'bg-[#16A34A]'}`} /><DMText className="text-[1.16vh] font-bold tracking-[0.06em] text-[#6E6C88]">{wizardStep === 1 ? 'LIVE PREVIEW' : 'AI REEL PREVIEW'}</DMText><DMDiv className="flex-1" />{wizardStep === 2 && <DMText as="span" className="rf-preview-state-chip rounded-full bg-[#EEEAFB] px-[1vh] py-[0.22vh] text-[1.05vh] font-bold text-[#4338CA]">{isUploadingMedia ? 'PROCESSING' : hasUploadedPhotos ? 'READY' : 'WAITING FOR MEDIA'}</DMText>}</DMDiv>
                <DMDiv className="mt-[1.05vh] flex min-h-0 flex-1 items-center justify-center"><DMDiv className="rf-preview-phone relative w-full overflow-hidden bg-[#0F1024] shadow-[0_2.8vh_6.6vh_rgba(16,24,40,.28)]"><DMDiv className="absolute left-1/2 top-[1.65vh] z-20 h-[0.48vh] w-[6.2vh] -translate-x-1/2 rounded-full bg-white/20" /><DMDiv className="rf-preview-screen relative h-full overflow-hidden bg-gradient-to-br from-[#7767EF] via-[#4F46E5] to-[#17142C]">
                  {wizardStep === 2 && hasUploadedPhotos && !isUploadingMedia && <DMDiv className="absolute inset-0 bg-[#1B1830]">{uploadedMediaUrls.map((url, index) => <DMDiv key={`${url}-${index}`} className={`absolute inset-0 flex items-center justify-center overflow-hidden transition-opacity duration-500 ${index === selectedUploadedPhotoIndex ? 'z-[2] opacity-100' : 'z-[1] opacity-0'}`}><DMImage src={url} alt={`Reel preview slide ${index + 1}`} className="rf-phone-image h-full w-full object-contain" /></DMDiv>)}</DMDiv>}
                  {wizardStep === 2 && isUploadingMedia && <DMDiv className="absolute inset-0 flex flex-col items-center justify-center bg-[#17142C] px-[2vh]"><DMDiv className="mb-[2.2vh] grid w-full grid-cols-3 gap-[0.7vh]"><DMDiv className="rf-dark-shimmer h-[12vh] rounded-[1vh]" /><DMDiv className="rf-dark-shimmer h-[15vh] rounded-[1vh]" /><DMDiv className="rf-dark-shimmer h-[11vh] rounded-[1vh]" /></DMDiv><DMDiv className="rf-preview-glow flex h-[5.5vh] w-[5.5vh] items-center justify-center rounded-[1.4vh] bg-[#6D5AE6] text-white"><Sparkles className="h-[2.5vh] w-[2.5vh]" /></DMDiv><DMText className="mt-[1.3vh] text-center text-[1.25vh] font-semibold text-white">AI is preparing your reel preview…</DMText></DMDiv>}
                  <DMDiv className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-black/85" />
                  <DMText as="span" className="rf-preview-badge absolute left-[1vh] top-[1vh] rounded-full bg-[rgba(20,16,40,.55)] px-[1vh] py-[0.34vh] text-[1.05vh] font-bold uppercase tracking-[0.04em] text-white">{reelForm.style || 'Cinematic'}</DMText>
                  {wizardStep === 2 && hasUploadedPhotos && !isUploadingMedia && <DMText as="span" className="rf-preview-badge absolute right-[1vh] top-[1vh] flex items-center gap-[0.45vh] rounded-full bg-[rgba(20,16,40,.55)] px-[0.88vh] py-[0.34vh] text-[1vh] font-bold text-white"><Music2 className="h-[1.22vh] w-[1.22vh]" /> {previewBgmLabel}</DMText>}
                  {isPreviewPlaying && wizardStep === 2 && <DMDiv className="rf-equalizer absolute inset-0 z-[2] flex items-center justify-center gap-[0.55vh]"><i /><i /><i /><i /></DMDiv>}
                  {!isUploadingMedia && <DMButton type="button" onClick={() => setIsPreviewPlaying(value => !value)} className="rf-preview-play absolute left-1/2 top-1/2 z-[3] flex h-[5.55vh] min-h-0 w-[5.55vh] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[rgba(255,255,255,.92)] p-0 text-[#4338CA] shadow-[0_1.1vh_2.66vh_rgba(16,24,40,.25)]" aria-label={isPreviewPlaying ? 'Pause preview' : 'Play preview'}>{isPreviewPlaying ? <Pause className="h-[2.1vh] w-[2.1vh] fill-current" /> : <Play className="ml-[0.3vh] h-[2.22vh] w-[2.22vh] fill-current" />}</DMButton>}
                  {!isUploadingMedia && <DMDiv className="absolute bottom-0 left-0 right-0 z-[3] bg-gradient-to-b from-transparent to-[rgba(15,12,30,.85)] p-[1.33vh] text-white"><DMText className="text-[1.38vh] font-bold text-white">{reelForm.title || 'Your reel title'}</DMText>{wizardStep === 1 && reelForm.description && <DMText className="mt-[0.45vh] line-clamp-2 text-[1.1vh] text-white/85">{reelForm.description}</DMText>}{wizardStep === 2 && hasUploadedPhotos && <DMText className="mt-[0.33vh] text-[1.05vh] text-white/75">{previewTransitionLabel} · AI-timed to {uploadedMediaUrls.length} photos</DMText>}<DMDiv className="mt-[0.88vh] h-[0.33vh] overflow-hidden rounded-full bg-white/25"><DMDiv className={`h-full rounded-full bg-white transition-all duration-1000 ${isPreviewPlaying ? 'w-2/3' : 'w-1/4'}`} /></DMDiv>{wizardStep === 2 && !hasUploadedPhotos && <DMText className="mt-[0.88vh] text-[1.05vh] text-white/60">Add photos below to bring this preview to life</DMText>}{wizardStep === 2 && hasUploadedPhotos && <DMDiv className="mt-[0.88vh] flex flex-wrap gap-[0.44vh]">{uploadedMediaUrls.map((_, index) => <DMDiv key={index} className={`h-[0.55vh] w-[0.55vh] rounded-full transition-opacity ${index === selectedUploadedPhotoIndex ? 'bg-white' : 'bg-white/45'}`} />)}</DMDiv>}</DMDiv>}
                </DMDiv></DMDiv></DMDiv>
              </DMDiv>
            </DMDiv>

            <DMDiv className="mt-[1.33vh] flex shrink-0 items-center gap-[1vw] border-t border-[#F0EEF7] pt-[1.56vh]">
              <DMButton type="button" onClick={() => wizardStep === 1 ? resetReelWizard() : setWizardStep(1)} className="rounded-[12px] border border-[#D6D3E6] bg-white px-[1.25vw] py-[1.22vh] text-[1.3vh] font-semibold">{wizardStep === 1 ? 'Refresh' : 'Back to Details'}</DMButton><DMText className={`rf-wizard-hint flex-1 text-right ${wizardStep === 2 && hasUploadedPhotos ? 'text-[#16A34A]' : 'text-[#9B9AB3]'}`}>{wizardHint}</DMText>
              {wizardStep === 1 ? <DMButton type="button" disabled={!stepComplete || isLoadingStyles} onClick={goToNextStep} className="rf-primary flex items-center gap-[0.5vw] rounded-[12px] px-[1.5vw] py-[1.05vh] text-[1.35vh] font-semibold">{isLoadingStyles ? <><RefreshCw className="rf-spin h-[1.7vh] w-[1.7vh]" /> Loading styles…</> : <>Continue <ArrowRight className="h-[1.7vh] w-[1.7vh]" /></>}</DMButton> : <DMButton type="button" disabled={!canGenerate || isGeneratingReel || isEnhancingImage} onClick={handleGenerateReelFromForm} className="rf-primary flex min-w-[11vw] items-center justify-center gap-[0.5vw] rounded-[12px] px-[1.5vw] py-[1.05vh] text-[1.35vh] font-semibold">{isGeneratingReel ? <><RefreshCw className="rf-spin h-[1.7vh] w-[1.7vh]" /> Generating…</> : <><Film className="h-[1.7vh] w-[1.7vh]" /> Generate Reel</>}</DMButton>}
            </DMDiv>
          </DMDiv>
        </DMDiv>
        {typeof document !== 'undefined' && createPortal(<>
        <ImagePreviewModal imageUrl={previewImageUrl} alt="Uploaded reel image" onClose={() => setPreviewImageUrl('')} />
        {isGenerationInfoOpen && <DMDiv className="rf-modal-layer"><DMDiv role="dialog" aria-modal="true" className="rf-modal-card w-[29vw] min-w-[400px] p-[2vw] text-center"><DMDiv className="mx-auto flex h-[6.6vh] w-[6.6vh] items-center justify-center rounded-[18px] bg-gradient-to-br from-[#6D5AE6] to-[#4F46E5] text-white shadow-[0_0_0_9px_rgba(123,108,242,.08)]"><Sparkles className="rf-spin h-[2.8vh] w-[2.8vh] fill-current" /></DMDiv><DMText as="h2" className="mt-[1.3vh] text-[2vh] font-bold">Generating your reel</DMText><DMText className="mt-[0.7vh] text-[1.3vh] leading-relaxed text-[#6E6C88]">Reel generation may take up to 20 minutes. Once the reel is ready, you will receive a notification. You can continue with your work in the meantime.</DMText><DMDiv className="mt-[1.5vh] flex items-center gap-[0.7vw]"><DMDiv className="h-[0.9vh] flex-1 overflow-hidden rounded-full bg-[#ECEAF6]"><DMDiv className="rf-generation-progress h-full rounded-full bg-gradient-to-r from-[#6D5AE6] to-[#4338CA]" /></DMDiv><DMText className="text-[1.2vh] font-bold text-[#4338CA]">In progress</DMText></DMDiv><DMButton type="button" onClick={() => navigate('/digital-marketing/dashboard')} className="mt-[1.6vh] w-full rounded-[12px] border border-[#D6D3E6] bg-white py-[1.1vh] text-[1.3vh] font-semibold hover:border-[#C9C2F5] hover:text-[#4338CA]">Go to Dashboard</DMButton></DMDiv></DMDiv>}
        {isCategoryRequestOpen && <DMDiv className="rf-modal-layer"><DMButton type="button" className="absolute inset-0" onClick={() => setIsCategoryRequestOpen(false)} aria-label="Close category dialog" /><DMDiv role="dialog" aria-modal="true" className="rf-modal-card w-[29vw] min-w-[420px] p-[1.8vw]"><DMDiv className="flex items-center justify-between"><DMDiv className="flex h-[5.5vh] w-[5.5vh] items-center justify-center rounded-[14px] bg-[#FFF1E0] text-[#E07A3C]"><Plus className="h-[2.4vh] w-[2.4vh]" /></DMDiv><DMButton type="button" onClick={() => setIsCategoryRequestOpen(false)} className="rf-modal-close rounded-[9px] text-[#9B9AB3] hover:bg-[#F5F3FB]" aria-label="Close"><X className="h-[1.7vh] w-[1.7vh]" /></DMButton></DMDiv>{categoryRequestMode === 'category' && <DMDiv className="mt-[1.2vh]"><DMLabel className="text-[1.1vh] font-bold">CATEGORY NAME</DMLabel><DMInput value={customCategory} onChange={event => setCustomCategory(event.target.value)} placeholder="e.g. Alumni Meetups" className="rf-control mt-[0.55vh] text-[1.3vh]" /></DMDiv>}<DMDiv className="mt-[1vh]"><DMLabel className="text-[1.1vh] font-bold">SUB CATEGORY NAME</DMLabel><DMInput value={customSubcategory} onChange={event => setCustomSubcategory(event.target.value)} placeholder="e.g. Reunion Highlights" className="rf-control mt-[0.55vh] text-[1.3vh]" /></DMDiv><DMButton type="button" disabled={(categoryRequestMode === 'category' && !customCategory.trim()) || !customSubcategory.trim()} onClick={() => { const category = categoryRequestMode === 'subcategory' ? selectedCategory : customCategory.trim(); const subCategory = customSubcategory.trim(); setSelectedCategory(category); setSelectedSubcategory(subCategory); setReelForm(current => ({ ...current, category, subCategory, style: '' })); setIsCategoryRequestOpen(false); }} className="rf-reference-primary mt-[1.4vh] w-full rounded-[12px] py-[1.1vh] text-[1.3vh] font-semibold text-white disabled:opacity-45">Add</DMButton></DMDiv></DMDiv>}
        </>, document.body)}
      </DMDiv>
    );
  }

  return (
    <DMDiv
      as="main"
      className="flex h-full min-h-0 flex-col overflow-hidden bg-white px-[2.5vw] py-[2.2vh] font-inter text-gray-950"
    >
      <DigitalMarketingBreadcrumb
        className="mb-[3.2vh] shrink-0"
        items={[
          { label: 'Reels', path: REELS_BASE_PATH },
          { label: 'Generate Reel' },
        ]}
      />

      <DMDiv className="mb-[3.2vh] shrink-0">
        <DMText
          as="h1"
          className="text-[3.5vh] font-bold leading-tight text-gray-950"
        >
          Generate Reel
        </DMText>
        <DMText className="mt-[0.65vh] text-[1.65vh] font-medium leading-tight text-gray-600">
          Because every achievement deserves a wider audience.
        </DMText>
      </DMDiv>

      <DMDiv as="form" className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <DMDiv className="grid min-h-0 flex-1 grid-cols-2 gap-x-[1.8vw] overflow-hidden">
          <DMDiv as="section" className="min-h-0 overflow-y-auto pr-[1.1vw]">
            <DMDiv>
              <FieldLabel className={editWebsiteContentLabelClassName} required>
                Title
              </FieldLabel>
              <DMInput
                className={editWebsiteContentInputClassName}
                placeholder="Enter Title"
                value={reelForm.title}
                onChange={event =>
                  setReelForm(current => ({
                    ...current,
                    title: event.target.value,
                  }))
                }
              />
            </DMDiv>

            <DMDiv className="mt-[1.4vh]">
              <FieldLabel className={editWebsiteContentLabelClassName} required>
                Style
              </FieldLabel>
              <DMSelect
                className={editWebsiteContentInputClassName}
                value={reelForm.style || 'cinematic'}
                onChange={event =>
                  setReelForm(current => ({
                    ...current,
                    style: event.target.value,
                  }))
                }
              >
                {reelStyleOptions.map(option => (
                  <DMOption key={option.value} value={option.value}>
                    {option.label}
                  </DMOption>
                ))}
              </DMSelect>
            </DMDiv>

            <DMDiv className="mt-[1.4vh]">
              <FieldLabel className={editWebsiteContentLabelClassName} required>
                Hashtags
              </FieldLabel>
              <DMDiv className="flex h-[24vh] flex-col rounded-[10px] border border-gray-300 bg-white px-[1.1vw] py-[1.4vh]">
                <DMTextarea
                  className="min-h-0 flex-1 resize-none bg-transparent text-[1.75vh] font-medium leading-[1.35] text-gray-950 outline-none placeholder:text-gray-400"
                  placeholder="Enter hashtags"
                  value={reelForm.description}
                  onChange={event =>
                    setReelForm(current => ({
                      ...current,
                      description: event.target.value,
                    }))
                  }
                />
                <DMButton
                  type="button"
                  onClick={handleGenerateDescription}
                  disabled={
                    isGeneratingDescription ||
                    descriptionGenerationsRemaining <= 0
                  }
                  className="mx-auto mt-[1vh] flex h-[5.5vh] w-[19.8vw] items-center justify-center gap-[0.8vw] rounded-full bg-primary-main text-[1.75vh] font-semibold text-white disabled:opacity-60"
                >
                  <Sparkles className="h-[2.1vh] w-[2.1vh]" strokeWidth={2} />
                  {isGeneratingDescription
                    ? 'Generating...'
                    : descriptionGenerateButtonLabel}
                </DMButton>
              </DMDiv>
            </DMDiv>

            <DMDiv className="mt-[2vh]">
              <FieldLabel className={editWebsiteContentLabelClassName} required>
                Categories
              </FieldLabel>
              <DMDiv className="flex flex-wrap gap-[1.2vh]">
                {reelCategories.map(category => (
                  <DMButton
                    key={category.label}
                    type="button"
                    onClick={() => {
                      setSelectedCategory(category.label);
                      setReelForm(current => ({
                        ...current,
                        category: category.label,
                      }));
                    }}
                    className={`${reelFormChipClassName} ${selectedCategory === category.label
                        ? 'border-primary-main bg-primary-main text-white shadow-sm'
                        : 'border-gray-300 bg-white text-gray-700 hover:border-primary-main hover:text-primary-main'
                      }`}
                  >
                    <DMText as="span" className="mr-[0.35vw]">
                      {category.icon}
                    </DMText>
                    {category.label}
                  </DMButton>
                ))}
              </DMDiv>
            </DMDiv>
          </DMDiv>

          <DMDiv as="section" className="flex min-h-0 flex-col">
            <FieldLabel className={editWebsiteContentLabelClassName}>
              {hasUploadActivity
                ? 'Upload Photos (Max 10 images can be uploaded.)'
                : 'Upload Photos (Max 10)'}
            </FieldLabel>
            {!hasUploadActivity ? (
              <DMDiv
                className="flex min-h-[48vh] flex-1 flex-col items-center justify-center rounded-[10px] border border-dashed border-gray-300 bg-white text-center"
                onDragOver={event => event.preventDefault()}
                onDrop={async event => {
                  event.preventDefault();
                  await handleUploadFiles(
                    getFilesFromFileList(event.dataTransfer.files)
                  );
                }}
              >
                <DMText className="text-[2.2vh] font-bold leading-tight text-gray-950">
                  Choose a file or drag & drop it here
                </DMText>
                <DMText className="mt-[1.2vh] text-[1.75vh] font-medium leading-tight text-gray-500">
                  png, jpg, jpeg (Max 15mb)
                </DMText>
                {renderPhotoInput(
                  'Upload Photos',
                  `mt-[1.8vh] flex h-[6vh] w-[17vw] items-center justify-center gap-[0.8vw] rounded-full border border-primary-main bg-white text-[1.85vh] font-semibold text-primary-main ${isUploadingMedia
                    ? 'cursor-not-allowed opacity-60'
                    : 'cursor-pointer'
                  }`
                )}
              </DMDiv>
            ) : (
              <DMDiv className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[10px] bg-white px-[0.6vw] py-[0.8vh]">
                <DMDiv className="grid shrink-0 grid-cols-5 gap-x-[1.15vw] gap-y-[1.45vh]">
                  {uploadedMediaUrls.map((url, index) => (
                    <DMDiv
                      key={`${url}-${index}`}
                      className="relative aspect-[1.8] overflow-visible rounded-[8px]"
                    >
                      <DMButton
                        type="button"
                        onClick={() => setSelectedUploadedPhotoIndex(index)}
                        className={`block h-full w-full rounded-[8px] bg-gray-100 p-0 text-left transition-shadow ${selectedUploadedPhotoIndex === index
                            ? 'ring-2 ring-primary-main ring-offset-1'
                            : 'hover:ring-1 hover:ring-primary-main/50'
                          }`}
                        aria-label={`Select uploaded item ${index + 1}`}
                      >
                        <DMImage
                          src={url}
                          alt={
                            uploadedFileNames[index] || `Upload ${index + 1}`
                          }
                          className="h-full w-full rounded-[8px] object-cover"
                        />
                      </DMButton>
                      <DMButton
                        type="button"
                        onClick={() => handleRemoveUploadedPhoto(index)}
                        className="absolute right-[-0.45vh] top-[-0.55vh] flex h-[2vh] w-[2vh] items-center justify-center rounded-full bg-primary-main text-white shadow-sm"
                        aria-label={`Remove uploaded photo ${index + 1}`}
                      >
                        <X className="h-[1.2vh] w-[1.2vh]" strokeWidth={3} />
                      </DMButton>
                    </DMDiv>
                  ))}
                  {Array.from({ length: uploadingPhotoSlots }).map((_, index) =>
                    renderUploadSkeleton(index)
                  )}
                </DMDiv>

                <DMText
                  as="h2"
                  className="mt-[1.2vh] shrink-0 text-[1.85vh] font-bold leading-tight text-gray-950"
                >
                  Image Enhancing
                </DMText>
                <DMDiv className="relative mt-[0.7vh] min-h-0 flex-1 overflow-hidden rounded-[10px] bg-gray-100">
                  {selectedUploadedPhotoUrl ? (
                    <DMButton
                      type="button"
                      onClick={() =>
                        setPreviewImageUrl(selectedUploadedPhotoUrl)
                      }
                      className="h-full w-full"
                      aria-label="Preview selected uploaded image"
                    >
                      <DMImage
                        src={selectedUploadedPhotoUrl}
                        alt="Enhancing preview"
                        className="h-full w-full object-cover"
                      />
                    </DMButton>
                  ) : (
                    <DMDiv className="h-full w-full animate-pulse bg-gradient-to-r from-gray-100 via-gray-200 to-gray-100" />
                  )}
                  <DMButton
                    type="button"
                    onClick={handleEnhanceSelectedPhoto}
                    disabled={!selectedUploadedPhotoUrl || isUploadingMedia || isEnhancingImage}
                    className="absolute right-[1vw] top-[1.5vh] flex h-[4.6vh] w-[4.6vh] items-center justify-center rounded-full bg-white text-primary-main shadow-[0_6px_14px_rgba(0,0,0,0.16)]"
                    aria-label="Enhance selected image"
                  >
                    <Sparkles className={`h-[2.2vh] w-[2.2vh] ${isEnhancingImage ? 'animate-pulse' : ''}`} strokeWidth={2} />
                  </DMButton>
                  {isEnhancingImage && (
                    <DMDiv className="absolute inset-0 flex items-center justify-center bg-white/60 text-[1.5vh] font-semibold text-primary-main backdrop-blur-[1px]">
                      AI is enhancing your image...
                    </DMDiv>
                  )}
                </DMDiv>
                {renderPhotoInput(
                  canUploadMorePhotos
                    ? 'Upload Image'
                    : 'Maximum 10 Images Uploaded',
                  `mt-[1.1vh] flex h-[5.4vh] w-full shrink-0 items-center justify-center gap-[0.8vw] rounded-full border border-primary-main bg-white text-[1.7vh] font-semibold text-primary-main ${isUploadingMedia || !canUploadMorePhotos
                    ? 'cursor-not-allowed opacity-60'
                    : 'cursor-pointer'
                  }`
                )}
              </DMDiv>
            )}
            <DMText className="mt-[1vh] flex items-center gap-[0.4vw] text-[1.2vh] font-semibold text-primary-main">
              <Sparkles className="h-[1.6vh] w-[1.6vh]" strokeWidth={2} />
              Low quality images? Don't worry, Our AI Image Enhancer will take
              care of it!!
            </DMText>
            <DMDiv className="mt-auto flex shrink-0 justify-end pt-[3vh]">
              <DMButton
                type="button"
                onClick={handleGenerateReelFromForm}
                disabled={!canGenerate || isGeneratingReel || isEnhancingImage}
                className="h-[6.6vh] w-[21vw] rounded-full bg-primary-main text-[1.85vh] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isGeneratingReel ? 'Generating...' : 'Generate Reel'}
              </DMButton>
            </DMDiv>
          </DMDiv>
        </DMDiv>
        <ImagePreviewModal
          imageUrl={previewImageUrl}
          alt="Uploaded reel image"
          onClose={() => setPreviewImageUrl('')}
        />
      </DMDiv>
    </DMDiv>
  );
}

function ReelListPreview() {
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const reelId = Number(params.id || 0);
  const [reelItem, setReelItem] = React.useState<ReelItem | null>(
    location.state?.reelItem || null
  );
  const [isLoadingReel, setIsLoadingReel] = React.useState(Boolean(reelId));
  const [socialHubStatus, setSocialHubStatus] = React.useState<SocialHubStatus>({});
  const [isLoadingSocialHubStatus, setIsLoadingSocialHubStatus] = React.useState(true);
  const [selectedSharePlatforms, setSelectedSharePlatforms] = React.useState<string[]>([]);
  const [isScheduleOpen, setIsScheduleOpen] = React.useState(false);
  const [isDownloadOpen, setIsDownloadOpen] = React.useState(false);
  const [scheduleDate, setScheduleDate] = React.useState('');
  const [scheduleTime, setScheduleTime] = React.useState('');
  const [scheduleError, setScheduleError] = React.useState('');
  const [comingSoonTooltip, setComingSoonTooltip] = React.useState<{
    x: number;
    y: number;
  } | null>(null);
  const [shareFlowState, setShareFlowState] = React.useState<'idle' | 'progress' | 'share-success' | 'schedule-success'>('idle');
  const [shareProgress, setShareProgress] = React.useState(0);
  const [flowPlatforms, setFlowPlatforms] = React.useState<string[]>([]);

  React.useEffect(() => {
    if (!reelId) {
      setIsLoadingReel(false);
      return;
    }

    const fetchReelDetails = async () => {
      setIsLoadingReel(true);

      try {
        const response = await reelsApi.view(reelId);
        const details = mapReelRow(getReelDetails(response));
        setReelItem(details);
      } catch (error: any) {
        showGlobalModal(
          getApiErrorMessage(error, 'Unable to fetch reel details'),
          'error'
        );
        navigate(REELS_BASE_PATH);
      } finally {
        setIsLoadingReel(false);
      }
    };

    fetchReelDetails();
  }, [navigate, reelId]);

  React.useEffect(() => {
    let isActive = true;

    const fetchSocialHubStatus = async () => {
      setIsLoadingSocialHubStatus(true);

      try {
        const status = await reelsApi.getSocialHubStatus();
        if (isActive) setSocialHubStatus(status);
      } catch {
        if (isActive) setSocialHubStatus({});
      } finally {
        if (isActive) setIsLoadingSocialHubStatus(false);
      }
    };

    fetchSocialHubStatus();
    return () => {
      isActive = false;
    };
  }, []);

  const finishPreviewFlow = React.useCallback(() => navigate(REELS_BASE_PATH), [navigate]);

  React.useEffect(() => {
    if (shareFlowState !== 'progress') return;
    const timer = window.setInterval(
      () => setShareProgress(current => Math.min(90, current + 8)),
      180
    );
    return () => window.clearInterval(timer);
  }, [shareFlowState]);

  React.useEffect(() => {
    if (shareFlowState !== 'progress' || shareProgress < 100 || !reelItem) return;
    const existing = getReelClientStatuses()[reelItem.id]?.sharedPlatforms || [];
    saveReelClientStatus(reelItem.id, { sharedPlatforms: Array.from(new Set([...existing, ...flowPlatforms])) });
    setShareFlowState('share-success');
  }, [flowPlatforms, reelItem, shareFlowState, shareProgress]);

  React.useEffect(() => {
    if (shareFlowState !== 'share-success' && shareFlowState !== 'schedule-success') return;
    const timer = window.setTimeout(finishPreviewFlow, 1800);
    return () => window.clearTimeout(timer);
  }, [finishPreviewFlow, shareFlowState]);

  if (isLoadingReel) {
    return <ReelListPreviewSkeleton />;
  }

  if (!reelItem) {
    return null;
  }

  const designPlatforms = reelSharePlatforms;
  const clientStatus = getReelClientStatuses()[reelItem.id] || {};
  const apiSharedPlatforms = [reelItem.instagram && 'instagram', reelItem.facebook && 'facebook', reelItem.youtube && 'youtube', reelItem.whatsapp && 'whatsapp', reelItem.parentApp && 'parentApp', reelItem.facultyApp && 'facultyApp'].filter(Boolean) as string[];
  const sharedPlatforms = Array.from(new Set([...(clientStatus.sharedPlatforms || []), ...apiSharedPlatforms]));
  const scheduledPlatforms = clientStatus.scheduledPlatforms || [];
  const completedPlatforms = new Set([...sharedPlatforms, ...scheduledPlatforms]);
  const shareablePlatforms = designPlatforms.filter(platform => !completedPlatforms.has(platform.key));
  const isPlatformConnected = (platformKey: string) => {
    const socialHubKey = reelSocialHubPlatformKeys[platformKey];
    return !socialHubKey || Boolean(socialHubStatus[socialHubKey]?.status);
  };
  const isPlatformSelectable = (platformKey: string) => isPlatformConnected(platformKey);
  const selectableSharePlatforms = shareablePlatforms.filter(platform => isPlatformSelectable(platform.key));
  const selectedPlatformCount = selectedSharePlatforms.length;
  const areAllSelectablePlatformsSelected = selectableSharePlatforms.length > 0 && selectableSharePlatforms.every(platform => selectedSharePlatforms.includes(platform.key));
  const platformStatusCount = new Set([...sharedPlatforms, ...scheduledPlatforms, ...selectedSharePlatforms]).size;
  const toggleSharePlatform = (platformKey: string) => {
    if (!isPlatformSelectable(platformKey)) return;
    setSelectedSharePlatforms(current => current.includes(platformKey) ? current.filter(key => key !== platformKey) : [...current, platformKey]);
  };
  const shareSelectedPlatforms = async () => {
    const platformsToShare = selectedSharePlatforms.filter(isPlatformSelectable);
    if (!platformsToShare.length) return;
    setFlowPlatforms([...platformsToShare]);
    setShareProgress(8);
    setShareFlowState('progress');
    const platformApiNames: Record<string, string> = {
      instagram: 'INSTAGRAM',
      facebook: 'FACEBOOK',
      youtube: 'YOUTUBE',
      whatsapp: 'WHATSAPP',
      parentApp: 'PARENT_APP',
      facultyApp: 'FACULTY_APP',
    };

    try {
      const updates: Array<Record<string, any>> = [];
      for (const platform of platformsToShare) {
        updates.push(
          await reelsApi.updatePlatform(reelItem.id, platformApiNames[platform])
        );
      }
      const lastUpdate = updates[updates.length - 1] || {};
      setReelItem(current => current ? {
        ...current,
        instagram: Boolean(lastUpdate.instagram ?? (platformsToShare.includes('instagram') || current.instagram)),
        facebook: Boolean(lastUpdate.facebook ?? (platformsToShare.includes('facebook') || current.facebook)),
        youtube: Boolean(lastUpdate.youtube ?? (platformsToShare.includes('youtube') || current.youtube)),
        whatsapp: Boolean(lastUpdate.whatsapp ?? (platformsToShare.includes('whatsapp') || current.whatsapp)),
        parentApp: Boolean(lastUpdate.parent_app ?? (platformsToShare.includes('parentApp') || current.parentApp)),
        facultyApp: Boolean(lastUpdate.faculty_app ?? (platformsToShare.includes('facultyApp') || current.facultyApp)),
        status: 'posted',
      } : current);
      setShareProgress(100);
    } catch (error: any) {
      setShareFlowState('idle');
      setShareProgress(0);
      showGlobalModal(
        getApiErrorMessage(error, 'Unable to share reel'),
        'error'
      );
    }
  };
  const updateScheduleDate = (value: string) => {
    setScheduleDate(value);
    setScheduleError(getScheduleValidationError(value, scheduleTime));
  };
  const updateScheduleTime = (value: string) => {
    setScheduleTime(value);
    setScheduleError(getScheduleValidationError(scheduleDate, value));
  };
  const confirmSchedule = () => {
    if (!scheduleDate || !scheduleTime || !selectedPlatformCount) return;
    const validationError = getScheduleValidationError(scheduleDate, scheduleTime);
    if (validationError) {
      setScheduleError(validationError);
      return;
    }
    saveReelClientStatus(reelItem.id, { scheduledPlatforms: Array.from(new Set([...(clientStatus.scheduledPlatforms || []), ...selectedSharePlatforms])), scheduledDate: scheduleDate, scheduledTime: scheduleTime });
    setFlowPlatforms([...selectedSharePlatforms]);
    setScheduleError('');
    setIsScheduleOpen(false);
    setShareFlowState('schedule-success');
  };
  const useStandaloneDetailDesign = true;
  if (useStandaloneDetailDesign) {
    return (
      <DMDiv
        as="main"
        className="reel-reference-screen reel-preview-detail rf-enter relative flex h-full min-h-0 flex-col overflow-y-auto px-[1.7vw] py-[2vh] font-inter text-[#161328]"
        onPointerMove={event => {
          const target = (event.target as HTMLElement).closest<HTMLElement>(
            '[data-coming-soon]'
          );
          setComingSoonTooltip(
            target ? { x: event.clientX, y: event.clientY } : null
          );
        }}
        onPointerLeave={() => setComingSoonTooltip(null)}
      >
        <DMDiv className="flex shrink-0 items-center gap-[0.85vw] pt-[0.8vh]"><DMButton type="button" onClick={() => navigate(REELS_BASE_PATH)} className="rf-icon-button text-[#4F46E5]" aria-label="Back to reels"><ChevronLeft className="h-[2vh] w-[2vh]" /></DMButton><DMDiv className="min-w-0"><DMText as="h1" className="truncate text-[3vh] font-semibold leading-none tracking-[-0.02em]">{reelItem.title}</DMText><DMText className="mt-[0.55vh] text-[1.35vh] text-[#9B9AB3]">{reelItem.date} · {reelItem.style || 'Cinematic'}</DMText></DMDiv></DMDiv>
        <DMDiv className="mt-[2.2vh] grid min-h-[76vh] flex-none grid-cols-[21vw_1fr] gap-[1.6vw] pb-[2vh]">
          <DMDiv className="rf-preview-left flex min-h-0 flex-col gap-[1.8vh]">
            <DMDiv className="relative min-h-0 flex-1 overflow-hidden rounded-[20px] bg-[#1B1830] shadow-[0_20px_46px_rgba(16,24,40,.18)]">
              {reelItem.videoUrl ? <DMVideo src={reelItem.videoUrl} poster={reelItem.thumbnail} controls className="h-full w-full object-cover" /> : <><DMImage src={reelItem.thumbnail || sampleImg} alt={reelItem.title} className="h-full w-full object-cover" /><DMDiv className="absolute inset-0 flex items-center justify-center"><DMDiv className="flex h-[6vh] w-[6vh] items-center justify-center rounded-full bg-white/90 text-[#4338CA] shadow-lg"><Play className="ml-1 h-[2.7vh] w-[2.7vh] fill-current" /></DMDiv></DMDiv></>}
              <DMText className="rf-preview-style-chip absolute left-[1.1vh] top-[1.1vh] rounded-full px-[1.15vh] py-[.7vh] font-bold uppercase tracking-wider text-white">{reelItem.style || 'Cinematic'}</DMText>
              <DMDiv className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#0F0C1E]/85 to-transparent p-[1vw] pt-[4vh]"><DMDiv className="flex items-center gap-[0.35vw]"><DMDiv className="flex h-[1.9vh] w-[1.9vh] items-center justify-center rounded bg-[#4F46E5] text-[0.9vh] font-extrabold text-white">S</DMDiv><DMText className="text-[0.95vh] font-bold text-white">{reelItem.school || 'Soomuch Demo School'}</DMText></DMDiv><DMDiv className="mt-[0.9vh] h-[0.35vh] overflow-hidden rounded-full bg-white/30"><DMDiv className="h-full w-[36%] rounded-full bg-white" /></DMDiv></DMDiv>
            </DMDiv>
            <DMDiv className="rf-reference-card rf-preview-copy-card shrink-0 px-[1.55vh] py-[1.45vh]"><DMText className="rf-preview-copy-label font-bold tracking-[0.07em] text-[#9B9AB3]">DESCRIPTION</DMText><DMText className="rf-preview-description mt-[.7vh] line-clamp-3 leading-relaxed">{reelItem.description || 'No description added.'}</DMText><DMText className="rf-preview-copy-label mt-[1.25vh] font-bold tracking-[0.07em] text-[#9B9AB3]">HASHTAGS</DMText><DMText className="rf-preview-hashtags mt-[.65vh] truncate text-[#4338CA]">{reelItem.hashtags || 'No hashtags added.'}</DMText></DMDiv>
          </DMDiv>

          <DMDiv className="rf-reference-card flex min-h-0 flex-col overflow-hidden px-[2.22vh] py-[2vh]">
            <DMDiv className="flex shrink-0 items-center gap-[1vh]"><DMText as="h2" className="font-semibold">Platforms</DMText><DMDiv className="flex-1" /><DMText key={platformStatusCount} className={`rf-count-chip rounded-full px-[1.22vh] py-[.44vh] font-bold ${platformStatusCount ? 'bg-[#EAFBF1] text-[#16A34A]' : 'bg-[#F5F3FB] text-[#9B9AB3]'}`}>{platformStatusCount} of {designPlatforms.length} platforms</DMText></DMDiv>
            <DMDiv className="mt-[1.33vh] min-h-0 flex-1 overflow-y-auto pr-[.35vh]">
              {!!sharedPlatforms.length && <DMDiv><DMText className="rf-section-label">SHARED</DMText><DMDiv className="mt-[.89vh] grid grid-cols-4 gap-[1.11vh]">{sharedPlatforms.map(key => { const platform = designPlatforms.find(option => option.key === key); return platform ? <DMDiv key={key} className="rf-status-platform-card rf-status-shared"><Check className="absolute right-[.78vh] top-[.78vh] h-[1.67vh] w-[1.67vh] text-[#16A34A]" /><DMDiv className="rf-detail-platform-logo" style={{ background: platform.chip }}>{platform.mono}</DMDiv><DMText className="rf-detail-platform-name">{platform.name}</DMText></DMDiv> : null; })}</DMDiv></DMDiv>}
              {!!scheduledPlatforms.length && <DMDiv className={sharedPlatforms.length ? 'mt-[2vh]' : ''}><DMText className="rf-section-label">SCHEDULED</DMText><DMDiv className="mt-[.89vh] grid grid-cols-4 gap-[1.11vh]">{scheduledPlatforms.map(key => { const platform = designPlatforms.find(option => option.key === key); return platform ? <DMDiv key={key} className="rf-status-platform-card rf-status-scheduled"><CalendarDays className="absolute right-[.78vh] top-[.78vh] h-[1.67vh] w-[1.67vh] text-[#4F46E5]" /><DMDiv className="rf-detail-platform-logo" style={{ background: platform.chip }}>{platform.mono}</DMDiv><DMText className="rf-detail-platform-name">{platform.name}</DMText><DMText className="rf-detail-platform-state text-[#4338CA]">Scheduled · {formatScheduledDateTime(clientStatus.scheduledDate, clientStatus.scheduledTime)}</DMText></DMDiv> : null; })}</DMDiv></DMDiv>}
              {!!shareablePlatforms.length && <DMDiv className={sharedPlatforms.length || scheduledPlatforms.length ? 'mt-[2vh]' : ''}><DMDiv className="flex items-center"><DMText className="rf-section-label">SHARE TO</DMText><DMDiv className="flex-1" /><DMButton type="button" disabled={shareFlowState === 'progress' || !selectableSharePlatforms.length} onClick={() => setSelectedSharePlatforms(areAllSelectablePlatformsSelected ? [] : selectableSharePlatforms.map(platform => platform.key))} className="rf-select-all flex items-center gap-[.67vh] rounded-full px-[.78vh] py-[.44vh] font-semibold text-[#4338CA] disabled:cursor-not-allowed disabled:opacity-45"><DMDiv className={`flex h-[1.44vh] w-[1.44vh] items-center justify-center rounded-[.44vh] border-2 ${areAllSelectablePlatformsSelected ? 'border-[#4F46E5] bg-[#4F46E5]' : 'border-[#D6D3E6] bg-white'}`}>{areAllSelectablePlatformsSelected && <Check className="h-[.9vh] w-[.9vh] text-white" />}</DMDiv>{areAllSelectablePlatformsSelected ? 'Clear all' : 'Select all'}</DMButton></DMDiv><DMDiv className="mt-[.89vh] grid grid-cols-4 gap-[1.11vh]">{shareablePlatforms.map(platform => { const selected = selectedSharePlatforms.includes(platform.key); const uploading = shareFlowState === 'progress' && flowPlatforms.includes(platform.key); const connected = isPlatformConnected(platform.key); const isSocialPlatform = Boolean(reelSocialHubPlatformKeys[platform.key]); const disabled = shareFlowState === 'progress' || !connected; const platformState = uploading ? 'Uploading…' : !connected ? (isLoadingSocialHubStatus && isSocialPlatform ? 'Checking connection…' : 'Not connected') : platform.sub; return <DMDiv key={platform.key} className="rf-share-platform-card-shell"><DMButton type="button" disabled={disabled} aria-label={`${platform.name}: ${platformState}`} onClick={() => toggleSharePlatform(platform.key)} className={`rf-share-platform-card ${selected ? 'is-selected' : ''} ${uploading ? 'is-uploading' : ''} ${!connected ? 'is-disabled' : ''}`}><DMDiv className="rf-detail-platform-logo" style={{ background: platform.chip }}>{platform.mono}</DMDiv><DMText className="rf-detail-platform-name">{platform.name}</DMText><DMText className={`rf-detail-platform-state ${uploading ? 'font-semibold text-[#4338CA]' : !connected ? 'font-semibold text-[#77758D]' : 'text-[#9B9AB3]'}`}>{platformState}</DMText><DMDiv className={`absolute right-[.78vh] top-[.78vh] flex h-[1.67vh] w-[1.67vh] items-center justify-center rounded-full border-2 ${selected ? 'border-[#4F46E5] bg-[#4F46E5]' : 'border-[#D6D3E6] bg-white'}`}>{selected && <Check className="h-[.9vh] w-[.9vh] text-white" />}</DMDiv>{uploading && <DMDiv className="rf-uploading-bar absolute bottom-0 left-0 right-0 h-[.33vh] bg-gradient-to-r from-[#6D5AE6] to-[#4338CA]" />}</DMButton></DMDiv>; })}</DMDiv><DMDiv className="mt-[1.33vh] grid grid-cols-2 gap-[1vh]"><DMDiv data-coming-soon="" className="rf-coming-soon-trigger"><DMButton type="button" disabled aria-label="Schedule, coming soon" className="pointer-events-none flex w-full items-center justify-center gap-[.67vh] rounded-[12px] border border-[#D6D3E6] bg-white font-semibold opacity-45"><CalendarDays className="h-[1.67vh] w-[1.67vh]" />Schedule</DMButton></DMDiv><DMButton type="button" onClick={shareSelectedPlatforms} disabled={!selectedPlatformCount || shareFlowState === 'progress'} className="rf-reference-primary flex items-center justify-center gap-[.89vh] rounded-[12px] font-semibold text-white disabled:opacity-45"><Share2 className="h-[1.67vh] w-[1.67vh]" />Share Now</DMButton></DMDiv></DMDiv>}
            </DMDiv>
          </DMDiv>
        </DMDiv>

        {typeof document !== 'undefined' && comingSoonTooltip && createPortal(
          <DMDiv
            className="rf-coming-soon-tooltip"
            role="tooltip"
            style={{
              left: Math.min(comingSoonTooltip.x + 14, window.innerWidth - 110),
              top: Math.min(comingSoonTooltip.y + 16, window.innerHeight - 42),
            }}
          >
            Coming Soon
          </DMDiv>,
          document.body
        )}

        {typeof document !== 'undefined' && createPortal(<>
        {(shareFlowState === 'share-success' || shareFlowState === 'schedule-success') && <DMDiv className="rf-modal-layer"><DMDiv role="dialog" aria-modal="true" aria-live="polite" className="rf-modal-card w-[44vh] p-[3.33vh_3.11vh_2.89vh] text-center"><DMDiv className="rf-success-mark relative mx-auto h-[7.33vh] w-[7.33vh]"><DMDiv className="rf-success-ring absolute inset-0 rounded-full border-2 border-[#34D399]" /><DMDiv className="rf-success-ring rf-success-ring-delay absolute inset-0 rounded-full border-2 border-[#34D399]" /><DMDiv className="absolute inset-0 flex items-center justify-center rounded-full bg-gradient-to-br from-[#22C55E] to-[#15A34A] text-white shadow-[0_1.56vh_3.33vh_rgba(22,163,74,.36)]"><Check className="h-[3.56vh] w-[3.56vh]" strokeWidth={2.6} /></DMDiv></DMDiv><DMText as="h2" className="mt-[1.89vh] font-semibold">{shareFlowState === 'share-success' ? 'Reel shared successfully' : 'Reel scheduled successfully'}</DMText><DMText className="mt-[.67vh] leading-relaxed text-[#6E6C88]">{shareFlowState === 'share-success' ? `Your reel was shared to ${flowPlatforms.length} selected platform${flowPlatforms.length === 1 ? '' : 's'}.` : `It will be shared to ${flowPlatforms.length} platform${flowPlatforms.length === 1 ? '' : 's'} on ${scheduleDate}.`}</DMText><DMDiv className="mt-[2vh] h-[.44vh] overflow-hidden rounded-full bg-[#ECEAF6]"><DMDiv className="rf-success-redirect-bar h-full rounded-full bg-gradient-to-r from-[#22C55E] to-[#15A34A]" /></DMDiv><DMText className="mt-[.78vh] text-[#9B9AB3]">Redirecting to Reels…</DMText><DMButton type="button" onClick={finishPreviewFlow} className="mt-[1.22vh] min-h-0 bg-transparent py-[.5vh] font-semibold text-[#4338CA]">View now</DMButton></DMDiv></DMDiv>}
        {isScheduleOpen && <DMDiv className="rf-modal-layer"><DMButton type="button" className="absolute inset-0" onClick={() => setIsScheduleOpen(false)} aria-label="Close schedule dialog" /><DMDiv role="dialog" aria-modal="true" aria-labelledby="schedule-reel-title" className="rf-modal-card w-[31vw] min-w-[420px] p-[1.6vw]"><DMDiv className="flex items-start"><DMDiv className="flex-1"><DMText id="schedule-reel-title" as="h2" className="text-[2.2vh] font-semibold">Schedule Reel</DMText><DMText className="mt-[0.35vh] text-[1.3vh] text-[#6E6C88]">Pick a future date and time for your selected platforms</DMText></DMDiv><DMButton type="button" onClick={() => setIsScheduleOpen(false)} className="rf-modal-close rounded-[9px] bg-[#EEEAFB] text-[#4F46E5]" aria-label="Close"><X className="h-[1.6vh] w-[1.6vh]" /></DMButton></DMDiv><DMDiv className="mt-[1.5vh] grid grid-cols-2 gap-[0.8vw]"><DMDiv><DMLabel className="text-[1.05vh] font-bold tracking-wider text-[#6E6C88]">SCHEDULED DATE</DMLabel><DMInput type="date" min={getLocalDateInputValue()} value={scheduleDate} onChange={event => updateScheduleDate(event.target.value)} onClick={event => (event.currentTarget as HTMLInputElement & { showPicker?: () => void }).showPicker?.()} aria-invalid={Boolean(scheduleError)} aria-describedby={scheduleError ? 'schedule-date-time-error' : undefined} className={`rf-control mt-[0.6vh] text-[1.3vh] ${scheduleError ? 'border-[#DC2626] focus:border-[#DC2626]' : ''}`} /></DMDiv><DMDiv><DMLabel className="text-[1.05vh] font-bold tracking-wider text-[#6E6C88]">SCHEDULED TIME</DMLabel><DMInput type="time" min={scheduleDate === getLocalDateInputValue() ? getLocalTimeInputValue() : undefined} value={scheduleTime} onChange={event => updateScheduleTime(event.target.value)} onClick={event => (event.currentTarget as HTMLInputElement & { showPicker?: () => void }).showPicker?.()} aria-invalid={Boolean(scheduleError)} aria-describedby={scheduleError ? 'schedule-date-time-error' : undefined} className={`rf-control mt-[0.6vh] text-[1.3vh] ${scheduleError ? 'border-[#DC2626] focus:border-[#DC2626]' : ''}`} /></DMDiv></DMDiv>{scheduleError && <DMText id="schedule-date-time-error" role="alert" className="mt-[0.7vh] text-[1.15vh] font-semibold text-[#DC2626]">{scheduleError}</DMText>}<DMDiv className="mt-[1.3vh] rounded-[10px] border border-[#F0EEF7] bg-[#FAFAFC] p-[0.75vw] text-[1.15vh] text-[#6E6C88]">Scheduling to <b className="text-[#161328]">{selectedSharePlatforms.length}</b> selected platform(s).</DMDiv><DMDiv className="mt-[1.5vh] grid grid-cols-2 gap-[0.8vw]"><DMButton type="button" onClick={() => setIsScheduleOpen(false)} className="rounded-[12px] bg-[#F0EEF7] py-[1.05vh] text-[1.3vh] font-semibold">Cancel</DMButton><DMButton type="button" onClick={confirmSchedule} disabled={!scheduleDate || !scheduleTime || Boolean(scheduleError)} className="rf-reference-primary rounded-[12px] py-[1.05vh] text-[1.3vh] font-semibold text-white disabled:opacity-45">Confirm Schedule</DMButton></DMDiv></DMDiv></DMDiv>}
        {isDownloadOpen && <DMDiv className="rf-modal-layer"><DMButton type="button" className="absolute inset-0" onClick={() => setIsDownloadOpen(false)} aria-label="Close download dialog" /><DMDiv role="dialog" aria-modal="true" className="rf-modal-card w-[28vw] min-w-[390px] p-[1.7vw] text-center"><DMDiv className="mx-auto flex h-[6.2vh] w-[6.2vh] items-center justify-center rounded-[16px] bg-[#FFF1E0] text-[#E07A3C]"><AlertTriangle className="h-[2.8vh] w-[2.8vh]" /></DMDiv><DMText as="h2" className="mt-[1.2vh] text-[2.05vh] font-semibold">Download at your own risk</DMText><DMText className="mt-[0.55vh] text-[1.25vh] leading-relaxed text-[#6E6C88]">This file may be large and is provided as-is. Soomuch is not responsible for how the downloaded reel is used or shared afterward.</DMText><DMDiv className="mt-[1.5vh] grid grid-cols-2 gap-[0.8vw]"><DMButton type="button" onClick={() => setIsDownloadOpen(false)} className="rounded-[12px] bg-[#F0EEF7] py-[1.05vh] text-[1.3vh] font-semibold">Cancel</DMButton><DMButton type="button" onClick={() => { if (reelItem.videoUrl) window.open(reelItem.videoUrl, '_blank', 'noopener,noreferrer'); setIsDownloadOpen(false); }} className="rf-reference-primary rounded-[12px] py-[1.05vh] text-[1.3vh] font-semibold text-white">Download Anyway</DMButton></DMDiv></DMDiv></DMDiv>}
        </>, document.body)}
      </DMDiv>
    );
  }

  return (
    <DMDiv
      as="main"
      className="flex h-full min-h-0 flex-col overflow-hidden bg-[#F7F8FB] px-[2.2vw] py-[2.4vh] font-inter text-gray-950"
    >
      <DigitalMarketingBreadcrumb
        className="mb-[1.8vh] shrink-0"
        items={[
          { label: 'Reels', path: REELS_BASE_PATH },
          { label: 'Preview Reel' },
        ]}
      />
      <DMDiv className="flex min-h-0 flex-1 w-full gap-[2vw]">
        <DMDiv
          as="section"
          className="flex min-h-0 flex-[0.95] items-center justify-center overflow-hidden rounded-[8px] border border-gray-200 bg-[#111827] p-[2.2vh] shadow-[0_18px_45px_rgba(15,23,42,0.16)]"
        >
          <DMDiv className="aspect-[9/16] h-full max-h-full max-w-full overflow-hidden rounded-[8px] bg-black shadow-[0_16px_35px_rgba(0,0,0,0.28)]">
            {reelItem.videoUrl ? (
              <DMVideo
                src={reelItem.videoUrl}
                poster={reelItem.thumbnail}
                className="h-full w-full object-cover"
                controls
              />
            ) : (
              <DMDiv className="relative h-full w-full">
                <DMImage
                  src={reelItem.thumbnail || sampleImg}
                  alt={reelItem.title}
                  className="h-full w-full object-cover"
                />
                <DMDiv className="absolute inset-0 flex items-center justify-center">
                  <PlayButton />
                </DMDiv>
              </DMDiv>
            )}
          </DMDiv>
        </DMDiv>

        <DMDiv
          as="section"
          className="flex min-h-0 flex-1 flex-col rounded-[8px] border border-gray-200 bg-white p-[2.4vh] shadow-[0_12px_30px_rgba(15,23,42,0.08)]"
        >
          <DMDiv className="flex shrink-0 items-center justify-between gap-[1vw]">
            <DMButton
              type="button"
              onClick={() => navigate(REELS_BASE_PATH)}
              className="flex h-[4.6vh] items-center gap-[0.5vw] rounded-full border border-gray-200 bg-white px-[1vw] text-[1.55vh] font-semibold text-gray-700 transition-colors hover:border-primary-main hover:text-primary-main"
            >
              <ChevronLeft className="h-[1.9vh] w-[1.9vh]" strokeWidth={2.2} />{' '}
              Back
            </DMButton>
            <DMText
              as="span"
              className={`rounded-full px-[1vw] py-[0.8vh] text-[1.35vh] font-bold uppercase tracking-[0.08em] ${reelItem.status === 'posted'
                  ? 'bg-emerald-50 text-emerald-700'
                  : 'bg-amber-50 text-amber-700'
                }`}
            >
              {reelItem.status === 'posted' ? 'Posted' : 'Generated'}
            </DMText>
          </DMDiv>

          <DMDiv className="mt-[2vh] shrink-0">
            <DMText className="text-[1.45vh] font-bold uppercase tracking-[0.08em] text-primary-main">
              Reel Preview
            </DMText>
            <DMText
              as="h1"
              className="mt-[0.8vh] max-h-[8vh] overflow-hidden text-[3.2vh] font-bold leading-[1.12] text-gray-950"
            >
              {reelItem.title}
            </DMText>
          </DMDiv>

          <DMDiv className="mt-[1.8vh] grid shrink-0 grid-cols-3 gap-[0.8vw]">
            <DMDiv className="rounded-[8px] border border-gray-200 bg-gray-50 px-[1vw] py-[1.3vh]">
              <DMText className="text-[1.2vh] font-semibold uppercase text-gray-400">
                Date
              </DMText>
              <DMText className="mt-[0.5vh] truncate text-[1.55vh] font-bold text-gray-900">
                {reelItem.date || '-'}
              </DMText>
            </DMDiv>
            <DMDiv className="rounded-[8px] border border-gray-200 bg-gray-50 px-[1vw] py-[1.3vh]">
              <DMText className="text-[1.2vh] font-semibold uppercase text-gray-400">
                Time
              </DMText>
              <DMText className="mt-[0.5vh] truncate text-[1.55vh] font-bold text-gray-900">
                {reelItem.time || '-'}
              </DMText>
            </DMDiv>
            <DMDiv className="rounded-[8px] border border-gray-200 bg-gray-50 px-[1vw] py-[1.3vh]">
              <DMText className="text-[1.2vh] font-semibold uppercase text-gray-400">
                Views
              </DMText>
              <DMText className="mt-[0.5vh] flex items-center gap-[0.4vw] text-[1.55vh] font-bold text-gray-900">
                <Eye className="h-[1.7vh] w-[1.7vh]" /> {reelItem.views}
              </DMText>
            </DMDiv>
          </DMDiv>

          <DMDiv className="mt-[1.8vh] shrink-0 rounded-[8px] border border-gray-200 bg-white px-[1vw] py-[1.4vh]">
            <DMText className="text-[1.2vh] font-semibold uppercase text-gray-400">
              School
            </DMText>
            <DMText className="mt-[0.4vh] truncate text-[1.6vh] font-bold text-gray-900">
              {reelItem.school || '-'}
            </DMText>
          </DMDiv>

          <DMDiv className="mt-[1.8vh] min-h-0 flex-1 overflow-hidden rounded-[8px] border border-gray-200 bg-gray-50 p-[1.2vw]">
            <DMText className="text-[1.2vh] font-semibold uppercase text-gray-400">
              Description
            </DMText>
            <DMText className="mt-[1vh] max-h-[15vh] overflow-hidden whitespace-pre-wrap text-[1.65vh] font-medium leading-[1.45] text-gray-700">
              {reelItem.description || 'No description added.'}
            </DMText>
          </DMDiv>
        </DMDiv>
      </DMDiv>
    </DMDiv>
  );
}

function ReelPreview() {
  const navigate = useNavigate();
  const location = useLocation();
  const reelForm: ReelFormValues = location.state?.reelForm || emptyReelForm;
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const handleGenerateReel = async () => {
    if (!reelForm.title || !reelForm.description) {
      showGlobalModal(
        'Complete required reel details before generating',
        'warning'
      );
      return;
    }

    setIsSubmitting(true);
    let generationStarted = false;

    try {
      const result = await reelsApi.generate({
        source: 'REEL',
        title: reelForm.title,
        description: reelForm.description,
        category: reelForm.category,
        sub_category: reelForm.subCategory,
        style: reelForm.style || 'cinematic',
        images:
          reelForm.mediaUrls || (reelForm.mediaUrl ? [reelForm.mediaUrl] : []),
      });
      if (result?.video_id) {
        savePendingReelGeneration({
          reelId: Number(result.reel_id || 0),
          videoId: result.video_id,
          title: reelForm.title,
        });
      }
      generationStarted = true;
      showGlobalModal('Reel generation started successfully');
    } catch (error: any) {
      showGlobalModal(
        getApiErrorMessage(
          error,
          'Reel generation started. Please check back later.'
        ),
        'warning'
      );
    } finally {
      setIsSubmitting(false);
      if (generationStarted) {
        navigate(REELS_BASE_PATH, {
          state: { showGeneratingReel: true, title: reelForm.title },
        });
      }
    }
  };

  return (
    <DMDiv
      as="main"
      className="min-h-full bg-white px-[2.5vw] py-[3.2vh] pb-[6vh] font-inter text-gray-950"
    >
      <DMButton
        type="button"
        onClick={() =>
          navigate(buildReelsPath('create'), { state: { reelForm } })
        }
        className="mb-6 flex shrink-0 items-center gap-2 text-sm font-medium text-[#5E4DE0] hover:underline"
      >
        <ChevronLeft size={16} /> Back to Edit Reel
      </DMButton>

      <DMDiv className="mb-8">
        <DMText as="h1" className="text-3xl font-bold text-gray-900">
          Preview Reel
        </DMText>
        <DMText className="text-gray-500">
          This is how your reel request will look before generation.
        </DMText>
      </DMDiv>

      <DMDiv className="mx-auto max-w-3xl rounded-2xl bg-white p-6 shadow-sm">
        <DMDiv className="mx-auto mb-8 aspect-[9/16] w-[22vw] min-w-[260px] overflow-hidden rounded-xl bg-gray-100">
          {reelForm.mediaUrl &&
            reelForm.mediaUrl.toLowerCase().endsWith('.mp4') ? (
            <DMVideo
              src={reelForm.mediaUrl}
              className="h-full w-full object-cover"
              controls
            />
          ) : (
            <DMImage
              src={reelForm.mediaUrl || sampleImg}
              alt={reelForm.title || 'Reel'}
              className="h-full w-full object-cover"
            />
          )}
        </DMDiv>
        <DMText
          as="h1"
          className="mb-4 text-3xl font-bold leading-tight text-gray-900"
        >
          {reelForm.title || 'Untitled Reel'}
        </DMText>
        <DMDiv className="mb-8 text-sm text-gray-500">
          {new Date().toLocaleDateString()} | DemoSchool
        </DMDiv>
        <DMDiv className="whitespace-pre-wrap leading-relaxed text-gray-700">
          {reelForm.description}
        </DMDiv>
      </DMDiv>

      <DMDiv className="mx-auto mt-4 flex w-full max-w-3xl justify-end gap-4">
        <DMButton
          type="button"
          onClick={handleGenerateReel}
          disabled={isSubmitting}
          className="flex min-w-[170px] items-center justify-center rounded-full bg-[#5E4DE0] px-10 py-3 font-medium text-white transition-colors hover:bg-[#4638C0] disabled:opacity-60"
        >
          {isSubmitting ? <SetupLoader height="h-5" /> : 'Generate Reel'}
        </DMButton>
      </DMDiv>
    </DMDiv>
  );
}

export function Reels() {
  const location = useLocation();
  const isCreate = location.pathname.endsWith('/create');
  const isCreatePreview = location.pathname.endsWith('/preview');
  const isListPreview = location.pathname.includes('/preview/');

  if (isListPreview) {
    return <ReelListPreview />;
  }

  if (isCreatePreview) {
    return <ReelPreview />;
  }

  return isCreate ? <ReelForm /> : <ReelsDashboard />;
}
